import {PRICING_COPY} from '../pricing/plans';
import {createFileRoute} from '@tanstack/react-router';
import {useEffect, useMemo, useRef, useState, useSyncExternalStore} from 'react';
import {apiAction, apiActionImproved, apiRetrieveImproved} from '../api';
import {ClubType, defaultSide, DrawType, eventSetsCount, isRacquetSport, LeagueType, ParticipantsType, quizFormatKeys, TEAM_SIDES, UserRole} from '../constants';
import {startGoogleSignIn} from '../utilities/googleAuth.ts';
import {config} from '../config/config.ts';
import {useEventPassReturn} from '../hooks/useEventPassReturn.ts';
import {useJwtAuth, useJwtUser} from '../JWTAuthProvider';
import services from '../services';
import {QUIZ_ENTRY_NOTE} from '../pricing/plans.ts';
import {suppressSetupTourOnce} from '../components/events/setupTourFlags.ts';
import {isPassGate, OrganizerPathValue, queuePostCreateOffer, quizOrganizerPath, withPathSafetyNet} from '../pricing/organizerPathRules.ts';
import {reportApiFailure} from '../utilities/errorReporter.ts';
import {isTeamBasedSport, sportDefinition, sportsForSurface, useSports} from '../sports/registry.ts';
import {sportAreaBase} from '../sports/sportAreas.ts';

// The sports this quiz offers, from the sport registry's 'quiz' surface (flag OFF: today's twelve).
// Read at call time, never at module load: on a first visit the module evaluates before the live
// registry has arrived, and a constant would freeze the snapshot's list for the whole session.
const quizSports = () => sportsForSurface('quiz');

/** The audience answer as the quiz persisted it — readable from the module-level build steps. */
const storedQuizAudience = (): string | undefined => {
    try { return JSON.parse(sessionStorage.getItem('sp_quiz_answers') || '{}').audience; } catch { return undefined; }
};
/** withPathSafetyNet's path setter for this funnel. */
const setQuizClubPath = (clubUid: string | undefined, path: OrganizerPathValue) => clubUid
    ? services.organizerPath.set(clubUid, path)
    : Promise.reject(new Error('Could not find your club.'));
import quizCardTournament from '../assets/quiz/quiz-tournament-card.webp';
import quizCardCreateGame from '../assets/quiz/quiz-create-game-card.webp';
import quizCardClubPlayers from '../assets/quiz/quiz-club-players-card.webp';
import quizCardLeaderboard from '../assets/quiz/quiz-leaderboard-card.webp';
import {setAdsUserData} from '../utilities/adsEmailMatching.ts';
import {
    clearGeneratorHandoff,
    GeneratorHandoff,
    needsOrganizerCompletion,
    readGeneratorHandoff,
    stashGeneratorTournament,
} from '../start/generatorHandoff.ts';

export const Route = createFileRoute('/quiz')({
    component: QuizPage,
});

/**
 * Ad-click quiz funnel: animated logo splash → 5 questions → offer screen → tournament BUILDER
 * (name + format + teams, no account needed) → "Generate draw" gates a minimal signup (name,
 * email, password only) → tournament + teams + draw are created. Quiz builds are NOT entry-capped:
 * a quiz signup creates a model-2 club, which EntryLimitService exempts from the PAYG 8-entry cap,
 * so any field size builds free. The step-9 pass screen and the entry_cap catch branches remain as
 * a safety net for a server-side 403, but no quiz path reaches them by design.
 *
 * Offerwall routing (see gatedBuild): every non-Americano build runs behind the walled
 * /quiz/activate/ URL — with no cap gate there is no pay screen to undercut, so watching the
 * video is the one price for every field size.
 *
 * Tracking: ViewContent (quiz start), Lead (pain answer), QuizComplete (all questions answered,
 * a custom event) and InitiateCheckout (offer screen) fire as deduplicated browser+server pairs;
 * CompleteRegistration and Purchase fire server-side on the real signup and Event Pass checkout.
 */

type Step = {q: string; sub: string; options: {val: string; label: string; emoji: string}[]};

const steps = (): Step[] => [
    {
        q: 'What are you organizing next?',
        sub: "Takes 30 seconds — we'll set things up around your answer.",
        options: [
            {val: 'tournament', label: 'A tournament', emoji: '🏆'},
            {val: 'league', label: 'A league season', emoji: '📅'},
            {val: 'ladder', label: 'A ladder or box league', emoji: '🪜'},
            {val: 'social', label: 'Americano', emoji: '🎉'},
        ],
    },
    {
        q: 'Which sport?',
        sub: "We'll match formats and scoring to your sport.",
        // Every sport the platform runs today, from the sport registry's 'quiz' surface. The quiz
        // RECORDS entry.value (e.g. 'pickleball'), which its analytics group on; the stored key is
        // looked up through SPORT_KEYS when the account and event are created.
        options: quizSports().map((entry) => ({
            val:   entry.value,
            label: entry.label,
            emoji: sportDefinition(entry.key)?.emoji || '🏅',
        })),
    },
    {
        q: "What's the biggest headache when you run events?",
        sub: 'Pick the one that costs you the most time.',
        options: [
            {val: 'payments', label: 'Chasing players for payment', emoji: '💸'},
            {val: 'signups', label: 'Collecting signups and player info', emoji: '📝'},
            {val: 'scores', label: 'Keeping scores and brackets updated', emoji: '📊'},
            {val: 'schedule', label: 'Sharing schedules and standings', emoji: '🗓️'},
        ],
    },
    {
        q: 'How do you handle event fees today?',
        sub: 'Be honest — this is where most organizers lose money.',
        options: [
            {val: 'cash', label: 'Cash on the day', emoji: '💵'},
            {val: 'transfer', label: 'Bank transfers I chase manually', emoji: '🏦'},
            {val: 'mixed', label: "A mix — it's messy", emoji: '🤯'},
            {val: 'free', label: 'My events are free to enter', emoji: '🆓'},
        ],
    },
    {
        q: 'How many teams in your next event?',
        sub: 'Last question — your setup is almost ready.',
        options: [
            {val: 'small', label: 'Up to 8 teams', emoji: '🎯'},
            {val: 'medium', label: '9 to 16 teams', emoji: '📈'},
            {val: 'large', label: '17 to 32 teams', emoji: '🔥'},
            {val: 'xl', label: 'More than 32', emoji: '🚀'},
        ],
    },
];

// Little momentum banners shown above the next question after each answer.
const UNLOCKS: Record<number, Record<string, string>> = {
    1: {
        tournament: '✓ Bracket & draw builder unlocked',
        league: '✓ Season standings & fixtures unlocked',
        ladder: '✓ Ladder challenges & rankings unlocked',
        social: '✓ Americano rotations unlocked',
    },
    2: {
        padel: '✓ Padel scoring formats loaded',
        pickleball: '✓ Pickleball scoring + DUPR support loaded',
        tennis: '✓ Tennis set scoring loaded',
        badminton: '✓ Badminton set scoring loaded',
        squash: '✓ Squash set scoring loaded',
        football: '✓ Football fixtures, tables & team management loaded',
        basketball: '✓ Basketball fixtures, tables & team management loaded',
        volleyball: '✓ Volleyball set scoring & team management loaded',
        cricket: '✓ Cricket fixtures, tables & team management loaded',
        esports: '✓ Esports series scoring & team management loaded',
        roundnet: '✓ Roundnet set scoring & team management loaded',
        other: '✓ 15+ sports supported — scoring loaded',
    },
    3: {
        payments: '✓ Automatic payment collection added to your setup',
        signups: '✓ Public registration page added to your setup',
        scores: '✓ Live scoring & auto-updating brackets added',
        schedule: '✓ Shareable schedule & standings page added',
    },
    4: {
        cash: '✓ Online entry fees — no more cash to count',
        transfer: '✓ Automatic payment links — no more chasing',
        mixed: '✓ One place for every entry fee',
        free: '✓ Free events run free on ScorePal',
    },
};

// Feature cards flashed between questions — a 5-second showcase of what the answers unlock,
// keyed by the question just answered so each card lands on the most relevant moment.
const INTERSTITIALS: Record<number, string> = {
    1: quizCardTournament,   // chose an event type → live groups & knockouts
    2: quizCardCreateGame,   // chose a sport → drag-players-on-court game builder
    3: quizCardClubPlayers,  // named their headache → fees/ratings/form roster
    4: quizCardLeaderboard,  // told us about fees → the reason players come back
};

// Lowercase display names by recorded value ('your sport' for Another sport), from the quiz surface.
const sportNames = (): Record<string, string> => Object.fromEntries(
    quizSports().map((entry) => [entry.value, entry.key === 'other' ? 'your sport' : sportDefinition(entry.key)?.label.toLowerCase() || entry.label.toLowerCase()]),
);
// Recorded value → stored sport_type key (note the platform's legacy 'pickelball' spelling).
const sportKeys = (): Record<string, string> => Object.fromEntries(quizSports().map((entry) => [entry.value, entry.key]));
// Sports entered as whole TEAMS rather than named players — the registry's team_based, which mirrors
// SportType::isTeamBased on the backend. Keyed by recorded VALUE, since answers hold values. These
// take the team-shaped builder football always had.
// With multi-sport on the sport's first side decides instead (Prompt 7): esports teams, roundnet pairs.
const teamSports = () => new Set(quizSports().filter((entry) => (firstSide(entry.key) !== null ? TEAM_SIDES.includes(firstSide(entry.key)!) : isTeamBasedSport(entry.key))).map((entry) => entry.value));

const firstSide = defaultSide;
const quizSetsCount = (sport?: string | null): number => eventSetsCount(sport, 1);

/** The formats the quiz offers for the sport (see quizFormatKeys). */
const quizFormats = (sport: string | null | undefined, teamShapedSport: boolean) => {
    const keys = quizFormatKeys(sport, teamShapedSport);
    return FORMATS.filter((f) => keys.includes(f.val));
};
// Team-shaped? A listed team sport, or a typed "Another sport" the visitor told us is played by
// teams (answers['2_shape']). A typed name we recognise follows its own sport's shape.
const teamShaped = (a: Record<string, string>): boolean => {
    if (a['2'] === 'other') {
        const resolved = resolveOtherSport(a['2_other']);
        return resolved === 'other' ? a['2_shape'] === 'teams' : teamSports().has(resolved);
    }
    return teamSports().has(a['2']);
};

// A typed "Other" sport saves as its real platform key when recognised, so a typed "Badminton"
// event carries the proper sport instead of a generic one. Only player-shaped sports are mapped:
// the Other flow builds players/pairs with sets scoring, and mapping a typed TEAM sport
// (baseball, cricket…) would hand a squad-and-scoreline sport a pairs draw. Those save as
// 'other' — the typed name still personalises the title.
const OTHER_SPORT_KEYS: Record<string, string> = {
    tennis: 'tennis', padel: 'padel', 'padel tennis': 'padel',
    pickleball: 'pickelball', pickelball: 'pickelball', 'pickle ball': 'pickelball',
    badminton: 'badminton', squash: 'squash', roundnet: 'roundnet', spikeball: 'roundnet',
};
const resolveOtherSport = (typed?: string): string =>
    OTHER_SPORT_KEYS[(typed || '').trim().toLowerCase().replace(/\s+/g, ' ')] || 'other';
/** The stored sport key the answers name (as the builder's sportKey reads it). */
const sportKeyOf = (a: Record<string, string>): string => (a['2'] === 'other' ? resolveOtherSport(a['2_other']) : (sportKeys()[a['2']] || 'padel'));
const EVENT_NAMES: Record<string, string> = {tournament: 'tournament', league: 'league season', ladder: 'ladder', social: 'social event'};

const PAIN_FEATURES: Record<string, string> = {
    payments: 'Entry fees collected online, upfront — payouts straight to your bank',
    signups: 'A public registration page — players sign themselves up',
    scores: 'Live scoring with brackets and standings that update themselves',
    schedule: 'A shareable page with the schedule, results and standings',
};

const FORMATS: {val: 'round_robin' | 'groups_knockout' | 'knockout' | 'americano'; label: string; desc: string}[] = [
    {val: 'round_robin', label: 'Round Robin', desc: 'Everyone plays everyone.'},
    {val: 'groups_knockout', label: 'Groups + Knockout', desc: 'Group stage, then a final bracket.'},
    {val: 'knockout', label: 'Knockout', desc: 'Single elimination bracket.'},
    {val: 'americano', label: 'Americano', desc: 'Social mixer — players swap partners each round, ranked individually.'},
];

const T_UID_KEY = 'sp_quiz_tuid';
const DRAW_DONE_KEY = 'sp_quiz_draw_done';
const STARTED_KEY = 'sp_quiz_started';
const PASS_OK_KEY = 'sp_quiz_pass_ok';
const SCHEDULE_DONE_KEY = 'sp_quiz_schedule_done'; // the league uid whose round robin the quiz already built
const USERS_DONE_KEY = 'sp_quiz_users_done';
const R_KEY = 'sp_quiz_rkey';
const RECOVERY_KEY = 'sp_quiz_recovery';

/**
 * Should this build run behind the Offerwall activation URL? Every field size: quiz builds are
 * uncapped (a quiz signup's club is model-2, exempt from the PAYG entry cap), so no build can
 * reach a pay screen and watching a video IS the price. Americano is always excluded: it has its
 * own activation flow at /americanos/activate/.
 */
const gatedBuild = (h: GeneratorHandoff): boolean =>
    h.eventKind !== 'americano';

// Real testimonials from the homepage TestimonialsSection (not invented copy) — auto-rotated.
const TESTIMONIALS = [
    {
        quote: 'Since introducing ScorePal at CF Tennis, participation in our leagues has noticeably increased. Players are more motivated knowing every match result instantly impacts the live standings. It\'s simple for our team to manage and brings extra energy to every competition.',
        name: 'Clark F.',
        role: 'Director, CF Tennis Academy',
    },
    {
        quote: 'ScorePal has completely elevated how we run leagues and tournaments at Love the Padel. Instead of manually tracking results and standings, everything updates automatically with live leaderboards our players can check in real time.',
        name: 'Alex S.',
        role: 'Founder, Love the Padel',
    },
    {
        quote: 'Inputting scores takes seconds and the live updates keep everyone in the loop. It actually adds a professional feel to our regular padel games and fuels the competitive spirit. I can\'t imagine going back to the spreadsheet I was using before!',
        name: 'Paul G.',
        role: 'Padel Chairman',
    },
];

// Module-scoped so StrictMode's dev remount (which resets refs) can't start a second concurrent
// build — the duplicate loops would double-add every team.
let createRunning = false;

// Splash decision survives StrictMode's double-effect (which would otherwise see the just-written
// "seen" flag on its re-run and skip the splash entirely). null = not decided this page load.
let splashDecided: boolean | null = null;

/**
 * Which screen this visit opens on, worked out during the FIRST RENDER rather than in an effect.
 *
 * An effect runs after the first paint, so a repeat visitor — who is meant to skip the splash —
 * used to paint one frame of it: the logo starting to spin, then gone. That reads as the start
 * screen failing to load rather than as a deliberate skip, which is exactly how it was reported.
 */
/** The opening phase never changes under the page; it is only re-read on mount. */
const subscribeNever = () => () => {};

/**
 * A build in progress on this device — a stashed quiz build with its started flag, or an Event
 * Pass return — opens on the progress screen, never the questions. The resume effect below
 * only runs once the sign-in check has finished, and a signup that came back through a real
 * page load (/quiz/activate/) showed the question step for those seconds before jumping to
 * the build; on a slow connection that read as being sent backwards.
 */
const resumingBuild = (): boolean => {
    if (typeof window === 'undefined') return false;
    if (new URLSearchParams(window.location.search).get('event_pass') === 'success') return true;
    return localStorage.getItem(STARTED_KEY) === '1' && readGeneratorHandoff()?.source === 'quiz';
};

const openingPhase = (): 'splash' | 'audience' | 'quiz' => {
    if (typeof window === 'undefined') return 'splash';

    let stashed: Record<string, string> = {};
    try { stashed = JSON.parse(sessionStorage.getItem('sp_quiz_answers') || '{}'); } catch { /* no stash */ }

    const handoff = window.location.hash === '#setup' && !!stashed['1'] && !!stashed['2'];
    if (splashDecided === null) splashDecided = !handoff && !localStorage.getItem('sp_splash_seen');
    if (splashDecided) return 'splash';

    // Same rule the effect applies: a handoff or a part-answered stash has answered the audience
    // question implicitly, so only a genuinely cold start is asked which funnel they are.
    return (handoff || ['1', '2', '3', '4', '5'].some((k) => !!stashed[k])) ? 'quiz' : 'audience';
};

// The async build outlives component instances (StrictMode mounts twins; auth changes can remount).
// Its UI updates go through this hook, re-registered by whichever instance is currently mounted,
// so progress and the pay/cap gates always render instead of landing on an unmounted twin.
let ui: {step: (n: number) => void; status: (s: string) => void; cap: (c: any) => void; err: (m: string) => void; done: (dest: string) => void} | null = null;

// Post-trim recovery: when the user declines the pass at the cap gate and trims to the free 8,
// the ORIGINAL field is kept here (with the created event's uid) so the done screen can offer the
// pass to restore the cut names. Expires after 48h so a stale offer never shadows a newer build.
type RecoveryStash = {uid: string; handoff: GeneratorHandoff; kept: number; ts: number};

function readRecovery(): RecoveryStash | null {
    try {
        const raw = localStorage.getItem(RECOVERY_KEY);
        if (!raw) return null;
        const rec = JSON.parse(raw) as RecoveryStash;
        if (!rec?.uid || !rec?.handoff?.teams?.length || !(rec.kept >= 1) || Date.now() - (rec.ts || 0) > 48 * 3600 * 1000) {
            localStorage.removeItem(RECOVERY_KEY);
            return null;
        }
        return rec;
    } catch { return null; }
}

function readCookie(name: string): string | null {
    const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
}

function uuid(): string {
    return (crypto as any).randomUUID ? (crypto as any).randomUUID() :
        'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.random() * 16 | 0;
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
}

// Browser pixel + server CAPI with a shared event_id (Meta dedupes on name+id). The CAPI relay is
// gated on the _fbp cookie: it only exists once the pixel has actually run, i.e. consent granted.
function fireEvent(name: string, customData: Record<string, any> = {}, dedupeKey = name, custom = false) {
    const fired: string[] = JSON.parse(sessionStorage.getItem('sp_quiz_fired') || '[]');
    if (fired.includes(dedupeKey)) return;
    sessionStorage.setItem('sp_quiz_fired', JSON.stringify([...fired, dedupeKey]));

    const eventId = uuid();
    const w = window as any;
    // Custom (non-standard) events must use trackCustom so Meta doesn't warn/misclassify them.
    if (w.fbq) w.fbq(custom ? 'trackCustom' : 'track', name, customData, {eventID: eventId});

    const fbp = readCookie('_fbp');
    if (!fbp) return; // pixel hasn't run (no consent yet) — skip the server relay too

    fetch(config.apiUrl + '/quiz/event', {
        method: 'POST',
        headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
        body: JSON.stringify({
            event_name: name,
            event_id: eventId,
            custom_data: customData,
            source_url: window.location.href.split('#')[0],
            fbp,
            fbc: readCookie('_fbc'),
        }),
        keepalive: true, // survives the redirect to signup/checkout
    }).catch(() => {});
}

/**
 * Message-match from an ad: /quiz/?sport=padel&format=tournament&src=… names the sport and the
 * format on the splash, pre-answers the first two questions (still changeable, and skippable with
 * one tap) and rides along on the quiz row so the signup carries it. Without params nothing changes.
 */
type QuizEntry = {sport: string; format: string; src: string | null};

let quizEntryRead: QuizEntry | null | undefined;

/** The client's answer, read once (useSyncExternalStore needs a stable snapshot); the server's is null. */
function readQuizEntry(): QuizEntry | null {
    if (quizEntryRead !== undefined) return quizEntryRead;
    quizEntryRead = readQuizEntryFromUrl();

    return quizEntryRead;
}

function readQuizEntryFromUrl(): QuizEntry | null {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const sport = (params.get('sport') || '').trim().toLowerCase().slice(0, 40);
    if (!sport) return null;
    const format = ((params.get('format') || '').trim().toLowerCase() || 'tournament').slice(0, 40);
    const src = (params.get('src') || '').trim().slice(0, 64) || null;
    return {sport, format, src};
}

/** The quiz answers the params stand for: '1' the format question, '2' the sport question — only when they name a real option. */
function preAnswersFor(entry: QuizEntry | null, steps: Step[]): Record<string, string> {
    if (!entry) return {};
    const out: Record<string, string> = {};
    const format = steps[0].options.find((o) => o.val === entry.format);
    if (format) out['1'] = format.val;
    const sport = steps[1].options.find((o) => o.val.toLowerCase() === entry.sport || o.label.toLowerCase() === entry.sport);
    if (sport) out['2'] = sport.val;
    return out;
}

// Full-funnel answer log (Admin → Quiz Responses): upserts one row per visitor after every answer,
// so abandoned quizzes are recorded too. The JWT (present from signup onwards) links the row to the
// account; the tournament uid is attached once the build creates it.
function recordResponse(payload: Record<string, any> = {}) {
    let key = localStorage.getItem(R_KEY);
    if (!key) {
        key = uuid();
        localStorage.setItem(R_KEY, key);
    }
    const token = localStorage.getItem('jwt_token');
    fetch(config.apiUrl + '/quiz/response', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(token ? {Authorization: 'Bearer ' + token} : {}),
        },
        body: JSON.stringify({key, source_url: window.location.href.split('#')[0], ...payload}),
        keepalive: true,
    }).catch(() => {});
}

// Fire the "your event is ready" email the moment a basic-mode quiz event is built (the scheduled
// events:welcome-emails command is the backstop). Fire-and-forget — never blocks the redirect.
function sendQuizWelcomeEmail(payload: {tournament_uid?: string; league_uid?: string}) {
    const token = localStorage.getItem('jwt_token');
    fetch(config.apiUrl + '/quiz/event-welcome', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(token ? {Authorization: 'Bearer ' + token} : {}),
        },
        body: JSON.stringify(payload),
        keepalive: true,
    }).catch(() => {});
}

export function QuizPage() {
    const jwt = useJwtAuth();
    const user = useJwtUser();
    // Subscribes to the live sport registry: the sport step re-renders with the full list once it lands.
    useSports();
    const STEPS = steps();

    // This component serves both /quiz/ and /quiz/activate/ (the Offerwall-gated activation URL —
    // see quiz.activate.tsx). Free builds only run on the activate path; getting there is always a
    // REAL page load so the AdSense Offerwall evaluates its URL rules.
    const onActivatePath = typeof window !== 'undefined' && window.location.pathname.includes('/quiz/activate');

    // step: 0..4 questions, 5 offer, 6 builder, 7 signup, 8 creating, 9 event-pass required
    // 'audience' sorts the two funnels before the questions begin: someone running a club all
    // season is served by /quiz-community, not by the one-event builder these five questions feed.
    // It is a phase rather than a sixth STEP so the answer keys (1-5) — which the #setup handoff
    // and the reporting both read — keep their meaning; only the displayed numbering shifts.
    // Hydrated with the server's answer (always the splash: the server has no storage), then
    // switched to this visitor's real opening screen before the first paint. Doing this with
    // useState(openingPhase) made the client's first render differ from the server's HTML, which
    // React reports as error #418 and recovers from by re-rendering the page from scratch.
    const opening = useSyncExternalStore(subscribeNever, openingPhase, () => 'splash' as const);
    const [phaseChosen, setPhase] = useState<'splash' | 'audience' | 'quiz' | null>(null);
    const phase = phaseChosen ?? opening;
    // Straight onto the progress screen on a resume (see resumingBuild); the server renders 0,
    // which never shows: the hydration pass is the splash on both sides.
    const [step, setStep] = useState(() => (resumingBuild() ? 8 : 0));
    // Null while hydrating (the server knows no URL), the params straight after — same shape as `opening`.
    const entry = useSyncExternalStore(subscribeNever, readQuizEntry, () => null);
    const entrySport = entry ? (STEPS[1].options.find((o) => o.val.toLowerCase() === entry.sport || o.label.toLowerCase() === entry.sport)?.label ?? entry.sport).toLowerCase() : '';
    const [answers, setAnswers] = useState<Record<string, string>>(() => {
        try { return JSON.parse(sessionStorage.getItem('sp_quiz_answers') || '{}'); } catch { return {}; }
    });
    // The params pre-answer the format and sport questions; an answer already given wins.
    useEffect(() => {
        if (!entry) return;
        setAnswers((current) => ({...preAnswersFor(entry, STEPS), ...current}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entry]);
    const [unlock, setUnlock] = useState('');

    // Builder state
    const [title, setTitle] = useState('');
    // The last title WE generated ("My Padel Tournament") — so a back-and-change of sport can
    // regenerate it without ever overwriting a title the user typed themselves.
    const autoTitle = useRef('');
    // "Set up now" is pre-selected so the form is visible immediately — a bare two-button screen
    // stalled ad traffic before the signup step. "Go to platform" stays one tap away and still
    // advances straight to the next step when chosen.
    const [mode, setMode] = useState<'basic' | 'advanced' | null>('basic');
    // Staged "preparing…" beat between the setup form and the account screen (logged-out only):
    // jumping straight from Generate to a signup form read as nothing having happened.
    const [prepStage, setPrepStage] = useState<number | null>(null);

    // Concierge (32+ events): what they need + contact details, delivered to the admin inbox.
    const [cNeeds, setCNeeds] = useState<string[]>([]);
    const [cDate, setCDate] = useState('');
    const [cSport, setCSport] = useState('');
    const [cType, setCType] = useState('');
    const [cCourts, setCCourts] = useState('');
    const [cPlayers, setCPlayers] = useState('');
    const [cDuration, setCDuration] = useState('');
    const [cName, setCName] = useState('');
    const [cEmail, setCEmail] = useState('');
    const [cNotes, setCNotes] = useState('');
    const [cBusy, setCBusy] = useState(false);
    const [cSent, setCSent] = useState(false);
    const [cErr, setCErr] = useState('');

    const toggleNeed = (n: string) => setCNeeds((prev) => prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]);

    // The player or team names for a concierge set-up: required, 32 or more (this branch is the
    // 32+ one), typed one per line or uploaded as a CSV / text file — the first column of each row.
    const C_MIN_ENTRANTS = 32;
    const [cEntrantsText, setCEntrantsText] = useState('');
    const cEntrants = useMemo(() => cEntrantsText.split(/\r?\n/)
        .map((line) => line.split(/[,;\t]/)[0].replace(/^["']|["']$/g, '').trim())
        .filter((n) => n && !/^(name|names|player|players|team|teams)$/i.test(n)), [cEntrantsText]);
    const uploadEntrants = (file: File | undefined) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => setCEntrantsText((prev) => [prev.trim(), String(reader.result ?? '').trim()].filter(Boolean).join('\n'));
        reader.readAsText(file);
    };

    const sendConcierge = async () => {
        if (cEntrants.length < C_MIN_ENTRANTS) {
            setCErr(cEntrants.length
                ? `Please add at least ${C_MIN_ENTRANTS} player or team names — you have ${cEntrants.length}.`
                : 'Please add your player or team names, or upload your list.');
            return;
        }
        if (!cName.trim() || !cEmail.trim()) { setCErr('Please add your name and email so we can reach you.'); return; }
        setCBusy(true); setCErr('');
        try {
            await apiActionImproved('quiz/concierge' as any, {
                name: cName.trim(), email: cEmail.trim(), needs: cNeeds,
                event_date: cDate || null, sport: cSport || null, tournament_type: cType || null,
                courts: cCourts || null, players: cPlayers || null, duration: cDuration || null,
                notes: cNotes.trim() || null, quiz_key: localStorage.getItem(R_KEY),
                entrants: cEntrants,
            });
            setCSent(true);
        } catch (e: any) {
            setCErr(e?.message || 'Could not send — please try again.');
        }
        setCBusy(false);
    };
    const [playType, setPlayType] = useState<'singles' | 'doubles'>('doubles');
    const [format, setFormat] = useState<'round_robin' | 'groups_knockout' | 'knockout' | 'americano'>('round_robin');
    const [leagueFormat, setLeagueFormat] = useState<'singles' | 'doubles_individual' | 'teams'>('doubles_individual');
    // Football league points table (win/draw allocation) — football's replacement for the
    // league-format picker.
    const [pointsWin, setPointsWin] = useState(3);
    const [pointsDraw, setPointsDraw] = useState(1);
    // A league's standings and fixtures: a points table or win % (null = the sport's default,
    // a table for team sports), and whether the quiz builds the round robin or the organiser
    // adds matches by hand.
    const [rankBy, setRankBy] = useState<'win_percent' | 'points' | null>(null);
    const [fixtures, setFixtures] = useState<'auto' | 'manual'>('auto');
    const [groupCount, setGroupCount] = useState(2);
    const [teamsText, setTeamsText] = useState('');
    const [builderError, setBuilderError] = useState('');
    const entrantsRef = useRef<HTMLTextAreaElement | null>(null);
    // The event's name has to be the organizer's own: the one we filled in ("My Padel Tournament")
    // is what their players would see on the live page, and nobody's event is called that.
    const [nameError, setNameError] = useState('');
    const nameRef = useRef<HTMLInputElement | null>(null);

    // Live platform stats for the offer screen's trust strip (same public endpoint as the homepage).
    const [stats, setStats] = useState<{matches: number; players: number; clubs: number; communities: number} | null>(null);

    // Rotating testimonial on the final "setup is ready" screen.
    const [tIdx, setTIdx] = useState(0);
    useEffect(() => {
        if (step !== 10) return;
        const iv = setInterval(() => setTIdx((i) => (i + 1) % TESTIMONIALS.length), 6000);
        return () => clearInterval(iv);
    }, [step]);

    // Signup state
    const [suName, setSuName] = useState('');
    const [suEmail, setSuEmail] = useState('');
    const [suPassword, setSuPassword] = useState('');
    const [suError, setSuError] = useState('');
    const [suBusy, setSuBusy] = useState(false);

    // Creation state
    const [status, setStatus] = useState('');
    const [capInfo, setCapInfo] = useState<{cap: number; count: number; reason: 'cap' | 'plan'} | null>(null);
    // Pass screen sequencing: the free alternatives are revealed only after declining the pass, so
    // an identical-outcome free card never sits beside the price undercutting it.
    const [showFreeOptions, setShowFreeOptions] = useState(false);

    // Exit intent: while the quiz is still in progress (questions → signup), a cursor leaving
    // through the top of the window on desktop, or the first back press on mobile, opens a
    // "keep going" prompt. Once per visit; never during the build or on the done screen.
    const [exitPrompt, setExitPrompt] = useState(false);
    // The signup step's version of the prompt asks WHY they're leaving (Admin → Quiz Responses).
    const [exitOther, setExitOther] = useState(false);
    const [exitNote, setExitNote] = useState('');
    const [exitThanks, setExitThanks] = useState(false);
    const answerExit = (reason: 'no_info' | 'unclear_value' | 'other' | 'continue') => {
        recordResponse({exit_reason: reason, ...(reason === 'other' ? {exit_note: exitNote.trim()} : {})});
        if (reason === 'continue') { setExitPrompt(false); return; }
        setExitThanks(true);
        setTimeout(() => setExitPrompt(false), 1600);
    };
    const exitGuardArmed = useRef(false);
    const quizInProgress = step <= 7;
    useEffect(() => {
        if (!quizInProgress) return;
        // Signup asks WHY and gets its own once-per-visit allowance: it is the funnel's most
        // expensive drop, and a "keep going" nudge earlier in the quiz must not use up the one
        // chance to ask there.
        const atSignup = step === 7;
        const shownKey = atSignup ? 'sp_quiz_exit_survey_shown' : 'sp_quiz_exit_shown';
        const alreadyShown = () => { try { return sessionStorage.getItem(shownKey) === '1'; } catch { return false; } };
        const markShown = () => { try { sessionStorage.setItem(shownKey, '1'); } catch { /* private mode */ } };
        const open = () => {
            if (alreadyShown()) return;
            markShown();
            setExitPrompt(true);
            // Recorded on being ASKED, answered or not — the denominator for every reason.
            if (atSignup) recordResponse({exit_asked: true});
            fireEvent('QuizExitIntent', {content_name: 'quiz_exit_intent', step}, 'QuizExitIntent', true);
        };

        const onMouseOut = (e: MouseEvent) => {
            if (e.relatedTarget === null && e.clientY <= 0) open();
        };
        // Back button: a guard entry sits on top of the history; the first back pops it and shows
        // the prompt (the entry is re-added so a second back really leaves).
        if (!exitGuardArmed.current) {
            exitGuardArmed.current = true;
            try { window.history.pushState({spQuizGuard: true}, ''); } catch { /* ignore */ }
        }
        const onPopState = () => {
            if (alreadyShown()) return;
            open();
            try { window.history.pushState({spQuizGuard: true}, ''); } catch { /* ignore */ }
        };

        document.addEventListener('mouseout', onMouseOut);
        window.addEventListener('popstate', onPopState);
        return () => {
            document.removeEventListener('mouseout', onMouseOut);
            window.removeEventListener('popstate', onPopState);
        };
    }, [quizInProgress, step]);
    const answeredCount = ['1', '2', '3', '4', '5'].filter((k) => !!answers[k]).length;
    useEffect(() => { if (step === 9) setShowFreeOptions(false); }, [step]);
    const [payBusy, setPayBusy] = useState(false);
    // Post-trim pass offer on the done screen (read fresh each time step 10 shows).
    const [recovery, setRecovery] = useState<RecoveryStash | null>(null);
    const [doneDest, setDoneDest] = useState(''); // where the "setup is ready" CTA goes (draws / league / setup)

    const sportKey = answers['2'] === 'other'
        ? resolveOtherSport(answers['2_other'])
        : (sportKeys()[answers['2']] || 'padel');
    // League answer builds a league (individual standings, no draw); everything else stays a tournament.
    const isLeague = answers['1'] === 'league';
    // Football: same builder, but team-sport shaped — named teams (no pairs), a points system
    // instead of a league format, and the finished event lives in the Football area.
    // Team sports share the football-shaped builder: named teams (no pairs), a points table for
    // leagues, no Americano. Each area sport's event LIVES in its own area (see sportAreaBase).
    const isTeamSport = teamShaped(answers);
    // Americano is a self-contained format with its own dedicated setup (players, courts, points),
    // so the quiz hands off to /americanos/create rather than building a draw here.
    const isAmericano = !isLeague && format === 'americano';
    const rawLines = teamsText.split('\n').map((t) => t.trim()).filter(Boolean);

    // Doubles entry, mobile-friendly: one player per line and every two names auto-pair — no "&"
    // typing needed. A line that already contains a pair separator (&, +, /, "and") stays as-is.
    // League singles is the only per-player entry; league doubles and teams enter pairs too.
    // Football never pairs: every line is one named team.
    const pairEntry = !isTeamSport && (isLeague ? leagueFormat !== 'singles' : playType === 'doubles');
    const rankByChosen: 'win_percent' | 'points' = rankBy ?? (isTeamSport ? 'points' : 'win_percent');
    let leftoverSingle: string | null = null;
    let teamNames: string[];
    if (pairEntry) {
        const pairs: string[] = [];
        let pending: string | null = null;
        for (const line of rawLines) {
            if (/[&+/]|\band\b/i.test(line)) {
                pairs.push(line);
            } else if (pending) {
                pairs.push(`${pending} & ${line}`);
                pending = null;
            } else {
                pending = line;
            }
        }
        leftoverSingle = pending;
        teamNames = pairs;
    } else {
        teamNames = rawLines;
    }


    // × on a chip: rebuild the textarea from the remaining entries (auto-paired names become
    // explicit "A & B" lines, which parse back identically).
    const removeEntry = (idx: number) => {
        const remaining = teamNames.filter((_, i) => i !== idx);
        setTeamsText([...remaining, ...(leftoverSingle ? [leftoverSingle] : [])].join('\n'));
    };

    // Register this instance as the build's UI sink (last mounted wins — see the module comment).
    useEffect(() => {
        ui = {step: setStep,
              // The pre-signup beat already played the build screens; after signup the real build
              // shows ONE brief "opening" state instead of replaying the same sequence.
              status: (s: string) => setStatus(s && sessionStorage.getItem('sp_prep_shown')
                  ? `Opening your ${isAmericano ? 'Americano' : isLeague ? 'league' : 'tournament'}…`
                  : s),
              cap: setCapInfo, err: setBuilderError,
              done: (dest: string) => { sessionStorage.removeItem('sp_prep_shown'); setDoneDest(dest); setStep(10); }};
    });

    // Splash: play once per visitor, skip on repeat visits. The decision is made once per page
    // load (module flag) so StrictMode's double-effect neither strands nor skips it; the timer is
    // rescheduled by the effect re-run after cleanup.
    useEffect(() => {
        // Handed over from /quiz-community ("Set up a new event now"): the answers it needs are
        // already stashed, so skip the splash and the questions and open the builder.
        const handoff = window.location.hash === '#setup' && !!answers['1'] && !!answers['2'];
        if (splashDecided === null) splashDecided = !handoff && !localStorage.getItem('sp_splash_seen');
        localStorage.setItem('sp_splash_seen', '1');
        // A handoff, a resume, or anyone already part-way through has answered the audience
        // question implicitly — only a cold start is asked.
        const next = (handoff || step > 0 || answeredCount > 0) ? 'quiz' : 'audience';
        if (!splashDecided) {
            setPhase(next);
            if (handoff && step === 0) startBuilder(answers);
            return;
        }
        const t = setTimeout(() => {
            splashDecided = false; // played — in-session revisits skip it
            setPhase(next);
        }, 7500);
        return () => clearTimeout(t);
    }, []);

    // Restore the builder state from a stash so counts and copy on later screens read correctly.
    const restoreFromHandoff = (stash: GeneratorHandoff) => {
        setTitle(stash.title || '');
        setFormat((stash.format as any) || 'round_robin');
        setTeamsText((stash.teams || []).join('\n'));
        if (stash.groupCount) setGroupCount(stash.groupCount);
        if (stash.playType === 'singles' || stash.playType === 'doubles') setPlayType(stash.playType);
        if (stash.leagueFormat) setLeagueFormat(stash.leagueFormat);
        if (stash.pointsPerWin != null) setPointsWin(stash.pointsPerWin);
        if (stash.pointsPerDraw != null) setPointsDraw(stash.pointsPerDraw);
        if (stash.rankBy) setRankBy(stash.rankBy);
        if (stash.fixtures) setFixtures(stash.fixtures);
        if (stash.mode === 'advanced') setMode('advanced');
    };

    // Resume: after the signup thank-you redirect (authed, stash present) or the Event Pass return,
    // jump straight back into the build instead of replaying the questions.
    useEffect(() => {
        if (!jwt.initialized || phase !== 'quiz') return;
        const stash = readGeneratorHandoff();
        if (localStorage.getItem(STARTED_KEY) !== '1' || !stash || stash.source !== 'quiz') return;
        restoreFromHandoff(stash);
        const passReturn = new URLSearchParams(window.location.search).get('event_pass') === 'success';
        if (passReturn) { setStep(8); setStatus('Confirming your Event Pass…'); return; } // useEventPassReturn resumes
        if (user) {
            // Back from "Continue with Google" with a bare socialite account, or an existing player
            // account with no club — finish the organizer signup (role + community) first, then
            // build. Otherwise the account is complete: build.
            if (needsOrganizerCompletion(user)) { completeGoogleSignup(stash); }
            else if (gatedBuild(stash) && !onActivatePath && localStorage.getItem(PASS_OK_KEY) !== '1') {
                // Guaranteed-free build resuming on /quiz/ — route through the Offerwall activation
                // URL first. Pass-paid builds (PASS_OK) and 9+ fields (cap gate ahead) skip it.
                window.location.assign('/quiz/activate/');
            }
            else { runCreate(stash); }
        }
        else { setStep(7); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jwt.initialized, phase, user?.uid]);

    // Event Pass Stripe return: confirm + apply the pass to the stored event, then resume.
    // The stored uid is a league ('el-…') on the league route — apply must use the matching key.
    // Mid-build (T_UID_KEY still set) resumes the build; otherwise this is a post-trim recovery
    // purchase from the done screen — restore the saved overflow to the finished event.
    const storedUid = typeof window !== 'undefined' ? localStorage.getItem(T_UID_KEY) : null;
    const passUid = storedUid || (typeof window !== 'undefined' ? readRecovery()?.uid || null : null);
    useEventPassReturn(passUid ? (passUid.startsWith('el-') ? {league_uid: passUid} : {tournament_uid: passUid}) : null, () => {
        if (storedUid) {
            localStorage.setItem(PASS_OK_KEY, '1'); // paid + applied — don't gate the draw again
            const stash = readGeneratorHandoff();
            if (stash) runCreate(stash);
            return;
        }
        const rec = readRecovery();
        if (rec) runRecovery(rec);
    });

    // Recovery purchase return: the build already finished, so no stash/started flag exists and the
    // resume effect above won't fire — park on the progress screen and restore the ORIGINAL field so
    // the done screen reads the full count once runRecovery lands there.
    useEffect(() => {
        if (phase !== 'quiz') return;
        if (new URLSearchParams(window.location.search).get('event_pass') !== 'success') return;
        if (localStorage.getItem(STARTED_KEY) === '1') return; // mid-build return — resume effect handles it
        const rec = readRecovery();
        if (!rec) return;
        restoreFromHandoff(rec.handoff);
        setStep(8);
        setStatus('Confirming your Event Pass…');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase]);

    // AdSense Offerwall: /quiz/activate/ is targeted in AdSense (Privacy & messaging → Offerwall →
    // URL inclusions), so free builds get gated with a video there. The tag must be loaded
    // explicitly — the app-shell loader skips it for organizers (open gating marks them
    // subscribed) — and in free mode this ad IS the payment, hence loading before the build runs.
    useEffect(() => {
        if (!onActivatePath || !config.adsenseEnabled || typeof document === 'undefined'
            || window.location.hash === '#no-tracking') {
            return;
        }
        const scriptId = 'adsbygoogle-' + config.adsenseClient;
        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.async = true;
            script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${config.adsenseClient}`;
            script.crossOrigin = 'anonymous';
            document.head.appendChild(script);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Funnel events + the offer screen's live trust stats.
    useEffect(() => {
        if (phase === 'quiz' && step === 0) fireEvent('ViewContent', {content_name: 'quiz_start'});
        // funnel_events, so /quiz/ and /build/ compare on live traffic (signed_up is the server's).
        if (phase === 'quiz' && step === 0) services.build.funnel({event: 'quiz_started', funnel: 'quiz', key: localStorage.getItem(R_KEY) || undefined});
        if (phase === 'quiz' && step === 0 && entry) recordResponse({entry});
        // The pass offer now only appears at the entry-cap gate (step 9) — that's the checkout intent.
        if (step === 9) fireEvent('InitiateCheckout', {value: 4.99, currency: 'USD'});
        // Post-trim recovery offer: read fresh on every arrival at the done screen — runRecovery
        // clears the key before landing here, so a completed restore hides the card automatically.
        if (step === 10) setRecovery(readRecovery());
        // Pass screen (9) shows the same proof block as the done screen, so both need the numbers.
        if ((step === 9 || step === 10) && !stats) {
            fetch(config.apiUrl + '/public-stats', {headers: {Accept: 'application/json'}})
                .then((r) => (r.ok ? r.json() : null))
                .then((d) => { if (d && typeof d.players === 'number') setStats(d); })
                .catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, step]);

    // Feature-card interstitial between questions: which card is showing (question just answered),
    // and the pending advance it delays. Clicking the card skips the wait.
    const [interstitial, setInterstitial] = useState<number | null>(null);
    const interTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => () => { if (interTimer.current) clearTimeout(interTimer.current); }, []);
    // Warm all four cards while the first question is on screen, so no interstitial shows a
    // half-loaded image.
    useEffect(() => {
        if (phase === 'quiz') Object.values(INTERSTITIALS).forEach((src) => { new Image().src = src; });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase]);
    const skipInterstitial = () => {
        if (interTimer.current) clearTimeout(interTimer.current);
        setInterstitial((showing) => {
            if (showing != null) setStep(showing);
            return null;
        });
    };

    // "Another sport" on Q2 asks WHICH sport before moving on — the typed name rides along as
    // answers['2_other'] (branching still keys off the stable 'other' value) and personalises
    // the copy downstream instead of the generic "your sport".
    const [askOtherSport, setAskOtherSport] = useState(false);
    const [otherSport, setOtherSport] = useState('');
    // A typed sport we don't model tells us nothing about its shape, and the shape decides the
    // whole builder (named teams vs players/pairs, points table vs standings). So the typed name
    // is followed by one more question — players or teams — recorded as answers['2_shape'].
    const [askShape, setAskShape] = useState(false);
    const submitOtherSport = () => {
        const typed = otherSport.trim().slice(0, 40);
        if (!typed) return;
        // A typed name we DO recognise (e.g. "badminton") already carries its shape — no question.
        if (resolveOtherSport(typed) !== 'other') {
            setAskOtherSport(false);
            answer(2, 'other', {'2_other': typed});
            return;
        }
        setAskShape(true);
    };
    const submitOtherShape = (shape: 'players' | 'teams') => {
        setAskShape(false);
        setAskOtherSport(false);
        answer(2, 'other', {'2_other': otherSport.trim().slice(0, 40), '2_shape': shape});
    };

    /**
     * Record an answer that is not one of the five numbered questions (the audience split that
     * opens the quiz). It goes into the same answers state as the rest because the server replaces
     * the stored answers wholesale on each upsert — anything left out of a later write is lost.
     */
    const keepAnswer = (key: string, val: string) => {
        const next = {...answers, [key]: val};
        setAnswers(next);
        sessionStorage.setItem('sp_quiz_answers', JSON.stringify(next));
        recordResponse({answers: next});
    };

    const answer = (stepNo: number, val: string, extra?: Record<string, string>) => {
        const next = {...answers, [String(stepNo)]: val, ...extra};
        setAnswers(next);
        sessionStorage.setItem('sp_quiz_answers', JSON.stringify(next));
        // Completion means the five questions are answered — side keys like '2_other' must not count.
        recordResponse({answers: next, completed: ['1', '2', '3', '4', '5'].every((k) => !!next[k])});
        if (stepNo === 3) fireEvent('Lead', {content_category: val});
        setUnlock(UNLOCKS[stepNo]?.[val] || '');
        // 32+ events get a human on offer: a chooser first — self-serve or ScorePal-assisted.
        if (stepNo === 5 && val === 'xl') setStep(12);
        else if (stepNo === 5) startBuilder(next); // last question answered — straight into the builder
        else {
            // Between questions: flash the matching feature card for 5 seconds (tap to skip),
            // then land on the next question.
            setInterstitial(stepNo);
            if (interTimer.current) clearTimeout(interTimer.current);
            interTimer.current = setTimeout(() => {
                setInterstitial(null);
                setStep(stepNo);
            }, 5000);
        }
    };

    // Last answer → builder, seeded from the quiz answers. Everyone starts on the free plan; the
    // Event Pass is offered at the entry-cap gate when the field actually needs it.
    const startBuilder = (finalAnswers: Record<string, string>) => {
        sessionStorage.setItem('sp_quiz_plan', 'free');
        const sportName = (finalAnswers['2'] === 'other' && finalAnswers['2_other'])
            ? finalAnswers['2_other'].toLowerCase()
            : sportNames()[finalAnswers['2']] || '';
        // Quiz complete — all questions answered, entering the builder. A distinct custom event
        // (not CompleteRegistration, which fires server-side on real signup) so quiz-start →
        // quiz-complete is a clean, measurable funnel step for ad optimization.
        fireEvent('QuizComplete', {
            content_name: 'quiz_complete',
            content_category: finalAnswers['2'] || '',
            event_type: isLeague ? 'league' : 'tournament',
        }, 'QuizComplete', true);
        const sportPrefix = sportName ? sportName.charAt(0).toUpperCase() + sportName.slice(1) + ' ' : '';
        // Regenerate the default title when it is empty OR still OUR auto-title: a user who
        // answered Football, went back and switched to Padel used to keep "My Football
        // Tournament" on their padel event, because the old guard only checked for empty.
        // A title the user actually typed never matches autoTitle and is never touched.
        if (!title || title === autoTitle.current) {
            const t = `My ${sportPrefix}${isLeague ? 'League' : 'Tournament'}`;
            setTitle(t);
            autoTitle.current = t;
        }
        // "A social event or Americano" at step 1 lands straight on the Americano format —
        // except for football, which has no Americano: it stays a round robin of teams.
        const wanted = finalAnswers['1'] === 'social' && !teamShaped(finalAnswers) ? 'americano'
            : finalAnswers['5'] === 'small' ? 'round_robin' : 'groups_knockout';
        // Flag on: a format the sport does not offer falls back to its first (chess: round robin).
        const offered = quizFormats(sportKeyOf(finalAnswers), teamShaped(finalAnswers)).map((f) => f.val);
        setFormat(offered.includes(wanted) || !offered.length ? wanted : offered[0]);
        // Flag on: the sport's first side (chess, darts singles; pétanque, cornhole doubles).
        const side = firstSide(sportKeyOf(finalAnswers));
        const singlesFirst = side !== null ? (side === 'singles' || side === 'individual') : finalAnswers['2'] === 'tennis';
        setPlayType(singlesFirst ? 'singles' : 'doubles');
        // Football is team-entry throughout: named clubs on a points table, never players/pairs.
        setLeagueFormat(teamShaped(finalAnswers) ? 'teams' : singlesFirst ? 'singles' : 'doubles_individual');
        setStep(6);
    };

    // "Generate draw" — the signup gate. Stash the build so it survives redirects, then either
    // create right away (already signed in) or ask for the minimal account first.
    // modeOverride: "Go to platform" advances in the same click as choosing the mode, before the
    // state update lands — so it passes 'advanced' explicitly rather than reading stale state.
    const generateDraw = (modeOverride?: 'basic' | 'advanced') => {
        const m = modeOverride ?? mode;
        // Both routes need a real name — "Go to platform" creates the event in this same click.
        const name = title.trim();
        if (!name || name === autoTitle.current.trim() || /^my (\w+ )?(tournament|league|americano)$/i.test(name)) {
            setNameError(`Give your ${isLeague ? 'league' : isAmericano ? 'Americano' : 'tournament'} its own name — this is what your players will see.`);
            nameRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
            nameRef.current?.focus({preventScroll: true});
            return;
        }
        setNameError('');
        // Basic mode generates a draw and needs a field; Advanced goes to the full setup page, so
        // entrants can be added later. Americano collects its players on its own setup screen, so it
        // needs no names here.
        if (!isAmericano && m === 'basic' && teamNames.length < 2) {
            setBuilderError(isTeamSport
                ? 'Add at least 2 teams (one per line).'
                : pairEntry
                    ? 'Add at least 4 players (one name per line — every two make a pair).'
                    : 'Add at least 2 players (one per line).');
            // Take them to the field that needs filling in, rather than leaving them to find it.
            entrantsRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
            entrantsRef.current?.focus({preventScroll: true});
            return;
        }
        setBuilderError('');
        const stash: GeneratorHandoff = {
            source: 'quiz',
            format,
            sport: sportKey,
            teams: teamNames,
            title: title.trim() || (isAmericano ? 'My Americano' : isLeague ? 'My League' : 'My Tournament'),
            groupCount,
            qualifiers: 2,
            playType: isTeamSport ? 'teams' : playType,
            teamSport: isTeamSport,
            mode: m || 'basic',
            eventKind: isAmericano ? 'americano' : isLeague ? 'league' : 'tournament',
            leagueFormat: isTeamSport ? 'teams' : leagueFormat,
            ...(isLeague ? {
                rankBy: rankByChosen,
                fixtures,
                ...(rankByChosen === 'points' ? {pointsPerWin: pointsWin, pointsPerDraw: pointsDraw} : {}),
            } : {}),
        };
        stashGeneratorTournament(stash);
        localStorage.setItem(STARTED_KEY, '1');
        // A fresh build supersedes any old post-trim offer — but an error-retry of the SAME build
        // (same stored uid) must keep the blob, or the trim recovery dies with the retry click.
        const rec = readRecovery();
        if (rec && rec.uid !== localStorage.getItem(T_UID_KEY)) localStorage.removeItem(RECOVERY_KEY);
        if (user) {
            // Guaranteed-free builds run behind the Offerwall activation URL (real page load — the
            // wall only evaluates its URL rules on a page view). A 9+ field builds right here and
            // meets the cap gate un-walled.
            if (gatedBuild(stash) && !onActivatePath) {
                window.location.assign('/quiz/activate/');
                return;
            }
            runCreate(stash);
        }
        else {
            // Logged-out: walk the build screens at a believable pace (~8s), then the ready screen.
            // The flag survives the signup redirect so the post-signup build doesn't replay them.
            sessionStorage.setItem('sp_prep_shown', '1');
            setPrepStage(0);
            [1, 2].forEach((s) => setTimeout(() => setPrepStage(s), s * 2600));
            setTimeout(() => { setPrepStage(null); setStep(7); }, 2 * 2600 + 2800);
        }
    };

    // Minimal signup: name, email, password. Sport/community are derived from the quiz so the
    // account form stays three fields.
    const submitSignup = async (e: any) => {
        e.preventDefault();
        if (!suName.trim() || !suEmail.trim() || !suPassword) { setSuError('Please fill in every field.'); return; }
        setSuBusy(true); setSuError('');
        try {
            const res: any = await apiAction('signup', {
                type: 'organizer',
                landing_url: jwt.landingUrl,
                quiz_key: localStorage.getItem(R_KEY) || undefined, // links users_analytics to this quiz row
                platform: 'web',
                // The quiz shows no leaderboard tickbox, so it enrols the way the signup form's
                // pre-ticked box does — sending nothing reads as "opt out" on the server.
                profile: {email: suEmail.trim(), password: suPassword, name: suName.trim(), lastname: '', inviter_uid: '', leaderboard_enrolled: true},
                questionnaire: {
                    sport: sportKey,
                    elo_grade: null, how_long_playing: null, how_often: null, nationality: null,
                    // The organizer path, always sent so the club counts as having answered and is
                    // never stopped mid-funnel. The audience answer decides for every format —
                    // a one-off visitor's league is a one-off event too (see quizOrganizerPath).
                    club: {type: ClubType.Community, name: `${suName.trim()}'s Events`, country_code: '', city: '', city_coordinates: null, meta: {}, organizer_path: quizOrganizerPath(isAmericano ? 'americano' : isLeague ? 'league' : 'tournament', answers.audience)},
                },
            });
            if (res?.error) { setSuError(res.error); setSuBusy(false); return; }
            jwt.setFieldsFromResponse(res);
            recordResponse({}); // token is stored now — links this quiz row to the new account
            // Quiz signups already have their competition mid-build — the "create your first
            // competition" Get Started popup would interrupt the handoff, so mark it seen.
            sessionStorage.setItem('welcome_tutorial_seen', '"y"'); // useSessionStorage stores JSON
            if (res?.user?.uid) {
                apiActionImproved('users-update', {
                    uid: res.user.uid,
                    values: {version: (res.user.version || 0) + 1, ['meta->welcome_tutorial_seen']: true},
                }).catch(() => {});
            }
            // Fire the signup conversion inline (same events the /signup/thank-you/ page sends) —
            // navigating there would remount this page and orphan the in-flight build.
            const gtag = (window as any).gtag;
            if (typeof gtag === 'function' && window.location.hash !== '#no-tracking') {
                setAdsUserData(suEmail); // enhanced conversion matching — before the conversion event
                gtag('event', 'sign_up', {method: 'scorepal', signup_type: 'organizer'});
                // transaction_id = uid: Google's dedupe key, shared with /signup/thank-you/ — whose
                // per-uid fired flag we also set, so landing there later can't count this twice.
                gtag('event', 'conversion', {send_to: 'AW-17042305006/ec_JCKW6_8ocEO7fs74_', transaction_id: res?.user?.uid || ''});
                if (res?.user?.uid) sessionStorage.setItem(`sp_signup_fired:${res.user.uid}`, '1');
            }
            const stash = readGeneratorHandoff();
            // Freshly signed up on /quiz/ — a guaranteed-free build runs behind the Offerwall
            // activation URL, so hand over with a real page load (gtag beacons survive the unload).
            if (stash && gatedBuild(stash) && !onActivatePath) {
                window.location.assign('/quiz/activate/');
                return;
            }
            if (stash) runCreate(stash);
        } catch (err: any) {
            setSuError(err?.message || 'Sign up failed. Please try again.');
            setSuBusy(false);
        }
    };

    // "Continue with Google" return leg: the socialite callback created a bare EmptyAfterSignup
    // account — upgrade it to organizer (community derived from their Google name, sport from the
    // quiz) with the same bookkeeping as the password signup, then continue the build.
    const googleCompleting = useRef(false);
    const completeGoogleSignup = async (stash: GeneratorHandoff) => {
        if (googleCompleting.current || !user) return;
        googleCompleting.current = true;
        setStep(8);
        setStatus('Finishing your account…');
        try {
            const res: any = await apiAction('signup', {
                type: 'organizer',
                landing_url: jwt.landingUrl,
                quiz_key: localStorage.getItem(R_KEY) || undefined, // links users_analytics to this quiz row
                platform: 'web',
                profile: {email: user.email, password: '', name: user.name || '', lastname: '', inviter_uid: '', leaderboard_enrolled: true},
                questionnaire: {
                    sport: stash.sport || sportKey,
                    elo_grade: null, how_long_playing: null, how_often: null, nationality: null,
                    // Same rule as the password signup above. On this leg the build and the
                    // answers survive the Google round trip in the stash / session storage.
                    club: {type: ClubType.Community, name: `${(user.name || 'My').trim()}'s Events`, country_code: '', city: '', city_coordinates: null, meta: {}, organizer_path: quizOrganizerPath(stash.eventKind, answers.audience ?? storedQuizAudience())},
                },
            });
            if (res?.error) throw new Error(res.error);
            jwt.setFieldsFromResponse(res);
            recordResponse({}); // token now maps this quiz row to the completed account
            sessionStorage.setItem('welcome_tutorial_seen', '"y"'); // useSessionStorage stores JSON
            if (res?.user?.uid) {
                apiActionImproved('users-update', {
                    uid: res.user.uid,
                    values: {version: (res.user.version || 0) + 1, ['meta->welcome_tutorial_seen']: true},
                }).catch(() => {});
            }
            const gtag = (window as any).gtag;
            if (typeof gtag === 'function' && window.location.hash !== '#no-tracking') {
                setAdsUserData(user.email); // enhanced conversion matching — before the conversion event
                gtag('event', 'sign_up', {method: 'google', signup_type: 'organizer'});
                // Same dedupe pair as the password path: uid as transaction_id + the thank-you flag.
                const uid = res?.user?.uid || user.uid;
                gtag('event', 'conversion', {send_to: 'AW-17042305006/ec_JCKW6_8ocEO7fs74_', transaction_id: uid || ''});
                if (uid) sessionStorage.setItem(`sp_signup_fired:${uid}`, '1');
            }
            runCreate(stash);
        } catch (err: any) {
            setSuError(err?.message || 'Sign up failed. Please try again.');
            setStep(7);
            googleCompleting.current = false;
        }
    };

    // The draw runs on name-based teams, but each typed person should also exist as a real player
    // account (owner_uid = organizer) so they show on the organizer's Players page and count in the
    // admin panel — the same accounts the full UI creates. The accounts are then LINKED to their
    // team (p1/p2) so the Setup → Players tab shows who is in each pair instead of blank rows.
    // dedupe_by_name makes re-runs (retries, event-pass returns, recovery) reuse the same accounts.
    // Non-critical: a failed profile never blocks the draw.
    const ensurePlayerProfiles = async (uid: string) => {
        ui?.status('Creating player profiles…');
        // People are derived from the teams that ACTUALLY exist, not from what was typed. When the
        // entry cap cuts the field short, the over-cap lines never became teams, and minting
        // accounts for them would leave orphan players the organizer can't see or use.
        const created: any[] = await services.tournaments.teams(uid).catch(() => []);
        const people = new Map<string, string>();
        (created || []).forEach((t: any) => String(t.name || '')
            .split(/[&+/]|\band\b/i).map((s) => s.trim()).filter(Boolean)
            .forEach((n) => people.set(n.toLowerCase(), n)));

        let failed = 0;
        const uidByName = new Map<string, string>();
        for (const name of people.values()) {
            const [first, ...rest] = name.split(/\s+/);
            const p: any = await services.players.createAndAdd({first_name: first, last_name: rest.join(' '), display_name: name, dedupe_by_name: true}).catch(() => null);
            if (p?.uid) uidByName.set(name.toLowerCase(), p.uid);
            else failed++;
        }
        localStorage.setItem(USERS_DONE_KEY, '1');

        ui?.status('Linking players to their teams…');
        for (const t of (created || [])) {
            if (t.p1_uid) continue; // already linked (resume)
            const persons = String(t.name || '').split(/[&+/]|\band\b/i).map((s) => s.trim()).filter(Boolean);
            const p1 = uidByName.get((persons[0] || '').toLowerCase());
            const p2 = persons[1] ? uidByName.get(persons[1].toLowerCase()) : undefined;
            if (!p1) { failed++; continue; }
            await apiActionImproved('teams-update', {
                uid: t.uid,
                values: {version: (t.version || 0) + 1, p1_uid: p1, ...(p2 ? {p2_uid: p2} : {})},
            }).catch(() => { failed++; });
        }

        // Non-fatal — the event exists and the organizer can carry on — but never silent. Every
        // entrant left unlinked is one who can't be invited, registered or ranked, nothing re-runs
        // this step later, and swallowing these is exactly how events shipped looking complete
        // while being empty shells behind the names.
        if (failed > 0) {
            reportApiFailure('quiz/ensure-player-profiles', 0, `${failed} entrant(s) left unlinked for ${uid}`);
        }
    };

    // Generate the draw(s) matching the chosen format, sized to data.teams.
    const generateDrawsFor = async (uid: string, data: GeneratorHandoff) => {
        ui?.status('Generating the draw…');
        const count = data.teams.length;
        const current: any = await services.tournaments.find(uid);
        await services.tournaments.update(uid, {version: (current?.version || 1) + 1} as any);
        const draw = async (params: any) => {
            const r: any = await services.tournaments.createDraw({uid, draw: params} as any);
            // Keep the structured body (its `code`) on the error: a pass gate here is handed over
            // to the event page by the caller, never shown as a message (see isPassGate).
            if (r?.error) throw Object.assign(new Error(r.error), r);
        };
        if (data.format === 'knockout') {
            await draw({draw_type: DrawType.Elimination, teams_count: count, winners_count: 1, pools_count: 1, playoffs_count: 2, create_consolation_draw: false, disable_shuffle: false});
        } else if (data.format === 'round_robin') {
            await draw({draw_type: DrawType.RoundRobin, teams_count: count, winners_count: 1, pools_count: 1, playoffs_count: 0, create_consolation_draw: false, disable_shuffle: false});
        } else {
            const pools = Math.max(1, Math.min(data.groupCount || 2, Math.floor(count / 2)));
            await draw({draw_type: DrawType.RoundRobin, teams_count: count, winners_count: 1, pools_count: pools, playoffs_count: 0, create_consolation_draw: false, disable_shuffle: false});
            await draw({draw_type: DrawType.Elimination, teams_count: Math.max(2, pools * (data.qualifiers || 2)), winners_count: 1, pools_count: 1, playoffs_count: 2, create_consolation_draw: false, disable_shuffle: false});
        }
    };

    // Post-trim recovery: the user finished free with the capped field, then bought the pass from
    // the done screen. The pass is already applied (useEventPassReturn) — add the saved overflow
    // back and, as long as no score has been recorded meanwhile, rebuild the draw for the full field.
    const runRecovery = async (rec: RecoveryStash) => {
        if (createRunning) return;
        createRunning = true;
        const h = rec.handoff;
        const uid = rec.uid;
        const overflow = h.teams.slice(rec.kept);
        ui?.step(8);
        try {
            if (h.eventKind === 'league') {
                const isTeams = h.leagueFormat === 'teams';
                ui?.status(isTeams ? 'Adding your saved teams…' : 'Adding your saved players…');
                const have = new Set<string>();
                if (isTeams) {
                    const existing: any[] = await apiRetrieveImproved('leagues-teams', {league_uid: uid}).catch(() => []) as any;
                    (existing || []).forEach((t: any) => have.add(String(t.name || '').trim().toLowerCase()));
                } else {
                    const existing: any[] = await services.leagues.players(uid).catch(() => []);
                    (existing || []).forEach((p: any) => {
                        [p.display_name, `${p.name || ''} ${p.lastname || ''}`.trim()]
                            .filter(Boolean).forEach((n: string) => have.add(n.trim().toLowerCase()));
                    });
                }
                const entrantNames = isTeams || h.leagueFormat === 'singles'
                    ? overflow
                    : overflow.flatMap((line) => line.split(/[&+/]|\band\b/i).map((s) => s.trim()).filter(Boolean));
                for (const raw of entrantNames) {
                    const name = raw.trim();
                    if (!name || have.has(name.toLowerCase())) continue;
                    if (isTeams) {
                        await apiActionImproved('teams-create', {league_uid: uid, name});
                    } else {
                        const [first, ...rest] = name.split(/\s+/);
                        await services.players.createAndAdd({first_name: first, last_name: rest.join(' '), display_name: name, league_uid: uid});
                    }
                }
                localStorage.removeItem(RECOVERY_KEY);
                ui?.done(`${sportAreaBase(h.sport)}/leagues/${uid}/league?welcome=quiz`);
                return;
            }

            ui?.status('Adding your saved teams…');
            const existing: any[] = await services.tournaments.teams(uid).catch(() => []);
            const have = new Set((existing || []).map((t: any) => String(t.name || '').trim().toLowerCase()));
            for (const raw of overflow) {
                const name = raw.trim();
                if (!name || have.has(name.toLowerCase())) continue;
                await apiActionImproved('teams-create', {tournament_uid: uid, name});
            }
            if (h.playType !== 'teams') {
                await ensurePlayerProfiles(uid);
            }

            // Rebuild the draw for the full field — but never wipe a recorded score: if any game
            // already has a result, leave the stages alone (the field is in; the organizer can
            // regenerate from the Draws tab).
            const games: any[] = await services.games.get({tournament_uid: uid}).catch(() => []);
            const played = (games || []).some((g: any) => g?.sets?.length);
            if (!played) {
                ui?.status('Rebuilding the draw…');
                const st: any = await services.tournaments.stages(uid).catch(() => null);
                for (const stage of (st?.stages || [])) {
                    await apiAction('tournament-draw-delete', {draws_stage_uid: stage.uid});
                }
                await generateDrawsFor(uid, h);
            }
            localStorage.removeItem(RECOVERY_KEY);
            ui?.done(`${sportAreaBase(h.sport)}/tournaments/${uid}/draws?welcome=quiz`);
        } catch {
            // Paid but the rebuild hit a snag — the pass is applied and (some) teams are in, so
            // land on the event rather than stranding a paying user on the quiz. The recovery blob
            // stays put, so revisiting /quiz within 48h offers the restore again.
            window.location.href = h.eventKind === 'league' ? `${sportAreaBase(h.sport)}/leagues/${uid}/league` : `${sportAreaBase(h.sport)}/tournaments/${uid}/draws`;
        } finally {
            createRunning = false;
        }
    };

    // League build (quiz "league" answer): a Win% league with individual or team standings — no
    // draw to generate; standings build as scores are recorded. Same idempotent shape as the
    // tournament path: stored uid, name-dedupe, entry-cap gate, Event Pass gate.
    const runCreateLeague = async (data: GeneratorHandoff) => {
        const areaBase = sportAreaBase(data.sport);
        ui?.status('Creating your league…');
        const leaguePoints = (data.rankBy ?? ((data.teamSport ?? teamSports().has(data.sport)) ? 'points' : 'win_percent')) === 'points';
        let uid = localStorage.getItem(T_UID_KEY) || '';
        if (!uid) {
            // Safety net: a one-off club creates leagues like any other event now, so this only
            // fires for a club that never answered the path question (settled with the funnel's
            // own answer) or a server with one-off leagues switched off (moved to regular
            // events). Either way the create is retried once, silently — no switch button here.
            const res: any = await withPathSafetyNet(() => apiActionImproved('leagues-create', {
                title: data.title || 'My League',
                sport_type: data.sport || 'padel',
                // A points table or win % standings: the builder's choice, defaulting to a table
                // for team sports and win % for the players' sports.
                league_type: leaguePoints ? LeagueType.Points : LeagueType.WinPercent,
                ...(leaguePoints ? {points_per_win: data.pointsPerWin ?? 3, points_per_draw: data.pointsPerDraw ?? 1} : {}),
                // Scheduled: the quiz builds the round robin below and the organiser keeps Setup →
                // Schedule for more rounds. "Add matches myself" is a bespoke league: games recorded
                // as they are played (switchable in Setup → Basic).
                is_auto_scheduled: (data.fixtures ?? 'auto') === 'auto',
                participants_type: data.leagueFormat === 'teams' ? ParticipantsType.DoublesTeam
                    : data.leagueFormat === 'singles' ? ParticipantsType.Singles
                        : ParticipantsType.DoublesIndividual,
                sets_count: quizSetsCount(data.sport),
            }), setQuizClubPath, {fallbackPath: quizOrganizerPath('league', storedQuizAudience())});
            if (!res?.uid) throw new Error(res?.error || res?.message || 'Could not create the league');
            uid = res.uid;
            localStorage.setItem(T_UID_KEY, uid);
            recordResponse({tournament_uid: uid, mode: data.mode});
        }

        const isTeams = data.leagueFormat === 'teams';
        ui?.status(isTeams ? 'Adding your teams…' : 'Adding your players…');
        const have = new Set<string>();
        if (isTeams) {
            const existing: any[] = await apiRetrieveImproved('leagues-teams', {league_uid: uid}).catch(() => []) as any;
            (existing || []).forEach((t: any) => have.add(String(t.name || '').trim().toLowerCase()));
        } else {
            const existing: any[] = await services.leagues.players(uid).catch(() => []);
            (existing || []).forEach((p: any) => {
                [p.display_name, `${p.name || ''} ${p.lastname || ''}`.trim()]
                    .filter(Boolean).forEach((n: string) => have.add(n.trim().toLowerCase()));
            });
        }
        // Teams become fixed pairs by their line ("Anna & Ben"); doubles-individual splits each
        // pair line back into two individually-ranked players; singles lines are single players.
        const entrantNames = isTeams || data.leagueFormat === 'singles'
            ? data.teams
            : data.teams.flatMap((line) => line.split(/[&+/]|\band\b/i).map((s) => s.trim()).filter(Boolean));
        for (const raw of entrantNames) {
            const name = raw.trim();
            if (!name || have.has(name.toLowerCase())) continue;
            try {
                if (isTeams) {
                    await apiActionImproved('teams-create', {league_uid: uid, name});
                } else {
                    const [first, ...rest] = name.split(/\s+/);
                    await services.players.createAndAdd({first_name: first, last_name: rest.join(' '), display_name: name, league_uid: uid});
                }
            } catch (err: any) {
                if (err?.code === 'entry_cap') {
                    ui?.cap({cap: err.cap || 8, count: entrantNames.length, reason: 'cap'});
                    ui?.step(9);
                    createRunning = false;
                    return;
                }
                throw err;
            }
        }

        // The round robin, once the field is in: one pass, everyone plays everyone, dated from the
        // next matchday (league-schedule-create, the Setup → Schedule generator). Once per league,
        // so a re-run after the signup or Event Pass round-trip never doubles the fixtures. A
        // failure here is not fatal: the league is live and Setup → Schedule can build them.
        if ((data.fixtures ?? 'auto') === 'auto' && localStorage.getItem(SCHEDULE_DONE_KEY) !== uid) {
            ui?.status('Creating your fixtures…');
            try {
                const games: any[] = await apiRetrieveImproved('games-get', {league_uid: uid}).catch(() => []) as any;
                if (!(games || []).length) {
                    await apiActionImproved('league-schedule-create' as any, {
                        league_uid: uid, number_of_rounds: 1, games_per_day: 1, autofill_date: true,
                        weekdays: ['1', '2', '3', '4', '5', '6', '7'],
                    });
                }
                localStorage.setItem(SCHEDULE_DONE_KEY, uid);
            } catch (err: any) {
                if (err?.code === 'entry_cap') throw err;
            }
        }

        // Invite-to-earn plan: the full field is in (the pledge lifted the cap) — hand straight
        // over to Setup → Players so the organizer starts inviting. No draw exists on a league,
        // so nothing else is gated here.
        if (sessionStorage.getItem('sp_quiz_plan') === 'earn') {
            clearGeneratorHandoff();
            [T_UID_KEY, DRAW_DONE_KEY, STARTED_KEY, PASS_OK_KEY, USERS_DONE_KEY, SCHEDULE_DONE_KEY].forEach((k) => localStorage.removeItem(k));
            createRunning = false;
            suppressSetupTourOnce(); // sent to Players on purpose — the help guide waits
            window.location.assign(`${areaBase}/leagues/${uid}/setup/applications/`);
            return;
        }

        // Pass plan: collect payment before showing the league, mirroring the tournament path.
        if (sessionStorage.getItem('sp_quiz_plan') === 'pass' && localStorage.getItem(PASS_OK_KEY) !== '1') {
            ui?.status('Checking your Event Pass…');
            const st = await services.eventPass.status().catch(() => null);
            if (st && st.available > 0) {
                await services.eventPass.apply({league_uid: uid}).catch(() => {});
                localStorage.setItem(PASS_OK_KEY, '1');
            } else {
                ui?.cap({cap: 8, count: data.teams.length, reason: 'plan'});
                ui?.step(9);
                createRunning = false;
                return;
            }
        }

        clearGeneratorHandoff();
        [T_UID_KEY, DRAW_DONE_KEY, STARTED_KEY, PASS_OK_KEY, USERS_DONE_KEY, SCHEDULE_DONE_KEY].forEach((k) => localStorage.removeItem(k));
        if (data.mode !== 'advanced') sendQuizWelcomeEmail({league_uid: uid}); // fully set up — email now
        // One-off visitors over the free cover: the league page offers the pass once after
        // landing and keeps the banner up. A no-op for everyone else (the server decides).
        queuePostCreateOffer(uid);
        ui?.done(data.mode === 'advanced' ? `${areaBase}/leagues/${uid}/setup` : `${areaBase}/leagues/${uid}/league?welcome=quiz`);
    };

    // Idempotent create: reuses the stored tournament, only adds missing teams, generates the draw
    // once — safe to re-run after the signup redirect or the Event Pass round-trip.
    const runCreate = async (data: GeneratorHandoff) => {
        if (createRunning) return;
        // Americano has its own dedicated setup (players, courts, points, payment) — the quiz just
        // funnels the (now signed-in) organizer straight there rather than building anything.
        if (data.eventKind === 'americano') {
            clearGeneratorHandoff();
            [T_UID_KEY, DRAW_DONE_KEY, STARTED_KEY, PASS_OK_KEY, USERS_DONE_KEY, SCHEDULE_DONE_KEY].forEach((k) => localStorage.removeItem(k));
            window.location.href = '/americanos/create';
            return;
        }
        // Football events are built here like any other, but they LIVE in the gated Football
        // area — every handover path below prefixes its destination with /football.
        const areaBase = sportAreaBase(data.sport);
        createRunning = true;
        ui?.step(8);
        try {
            if (data.eventKind === 'league') {
                await runCreateLeague(data);
                return;
            }
            ui?.status('Creating your tournament…');
            let uid = localStorage.getItem(T_UID_KEY) || '';
            if (!uid) {
                // Safety net: a club that never answered the path question is settled with the
                // funnel's own answer and the create retried once, silently (see runCreateLeague).
                const res: any = await withPathSafetyNet(() => services.tournaments.create({
                    title: data.title || 'My Tournament',
                    sport_type: data.sport || 'padel',
                    tournament_type: data.format === 'knockout' ? 2 : 1,
                    draws_description: data.format === 'groups_knockout' ? 'Round Robin + Elimination'
                        : data.format === 'knockout' ? 'Elimination' : 'Round Robin',
                    // singles = 3, doubles/pairs = 1, whole teams = 2 (entrants stay name-based)
                    participants_type: data.playType === 'singles' ? 3 : data.playType === 'doubles' ? 1 : 2,
                    sets_count: quizSetsCount(data.sport),
                    visibility_type: 1,
                } as any), setQuizClubPath, {fallbackPath: quizOrganizerPath(data.eventKind, storedQuizAudience())});
                if (!res?.uid) throw new Error(res?.error || res?.message || 'Could not create the tournament');
                uid = res.uid;
                localStorage.setItem(T_UID_KEY, uid);
                recordResponse({tournament_uid: uid, mode: data.mode});
            }

            ui?.status('Adding your teams…');
            const existing: any[] = await services.tournaments.teams(uid).catch(() => []);
            const have = new Set((existing || []).map((t: any) => String(t.name || '').trim().toLowerCase()));
            for (const raw of data.teams) {
                const name = raw.trim();
                if (!name || have.has(name.toLowerCase())) continue;
                try {
                    await apiActionImproved('teams-create', {tournament_uid: uid, name});
                } catch (err: any) {
                    if (err?.code === 'entry_cap') {
                        // The cap cuts the field short, but the teams created BEFORE it still need
                        // their player accounts. Returning straight to the upgrade screen (as this
                        // used to) left every one of them a bare "Anna & Ben" label with nobody
                        // behind it — unable to be invited, registered or ranked — and since
                        // nothing re-runs the linking step, upgrading afterwards never repaired it.
                        if (data.playType !== 'teams') {
                            await ensurePlayerProfiles(uid).catch(() => {});
                        }
                        ui?.cap({cap: err.cap || 8, count: data.teams.length, reason: 'cap'});
                        ui?.step(9);
                        createRunning = false;
                        return;
                    }
                    throw err;
                }
            }

            // Each typed person also gets a real player account, linked to their team — see
            // ensurePlayerProfiles. Whole-team entry names aren't people, so those are skipped.
            if (data.playType !== 'teams') {
                await ensurePlayerProfiles(uid);
            }

            // "Get the event pass" plan: collect the $9.99 BEFORE the draw is shown. An already-owned
            // pass is applied silently; otherwise present checkout and resume on return.
            if (sessionStorage.getItem('sp_quiz_plan') === 'pass' && localStorage.getItem(PASS_OK_KEY) !== '1') {
                ui?.status('Checking your Event Pass…');
                const st = await services.eventPass.status().catch(() => null);
                if (st && st.available > 0) {
                    await services.eventPass.apply({tournament_uid: uid}).catch(() => {});
                    localStorage.setItem(PASS_OK_KEY, '1');
                } else {
                    ui?.cap({cap: 8, count: data.teams.length, reason: 'plan'});
                    ui?.step(9);
                    createRunning = false;
                    return;
                }
            }

            // Advanced mode: no auto-draw — hand over to the full Setup page (registration,
            // payments, scheduling, draws — every step configurable).
            if (data.mode === 'advanced') {
                clearGeneratorHandoff();
                [T_UID_KEY, DRAW_DONE_KEY, STARTED_KEY, PASS_OK_KEY, USERS_DONE_KEY, SCHEDULE_DONE_KEY].forEach((k) => localStorage.removeItem(k));
                ui?.done(`${areaBase}/tournaments/${uid}/setup`);
                return;
            }

            if (localStorage.getItem(DRAW_DONE_KEY) !== '1') {
                await generateDrawsFor(uid, data);
                localStorage.setItem(DRAW_DONE_KEY, '1');
            }

            clearGeneratorHandoff();
            [T_UID_KEY, DRAW_DONE_KEY, STARTED_KEY, PASS_OK_KEY, USERS_DONE_KEY, SCHEDULE_DONE_KEY].forEach((k) => localStorage.removeItem(k));
            sendQuizWelcomeEmail({tournament_uid: uid}); // draw generated + teams in — email the links now

            // Invite-to-earn plan: the draw above is generated and viewable, but scores stay locked
            // until 9+ players sign up — so hand over to Setup → Players, where the progress card,
            // the per-player invites and the registration link live.
            if (sessionStorage.getItem('sp_quiz_plan') === 'earn') {
                createRunning = false;
                suppressSetupTourOnce(); // sent to Players on purpose — the help guide waits
                window.location.assign(`${areaBase}/tournaments/${uid}/setup/applications/`);
                return;
            }

            // One-off visitors over the free cover: the event page opens the pass offer once on
            // landing and keeps the banner up. A no-op for everyone else (the server decides).
            queuePostCreateOffer(uid);
            ui?.done(`${areaBase}/tournaments/${uid}/draws?welcome=quiz`);
        } catch (err: any) {
            createRunning = false;
            ui?.status('');
            // The one-off pass gate must NEVER read as an error here: the quiz has no offer modal,
            // so it would be a dead end at the activation moment. The event exists and the field
            // is in — hand over to the event page, where the offer opens and the draw is one tap
            // away once it is settled.
            const builtUid = localStorage.getItem(T_UID_KEY);
            if (isPassGate(err) && builtUid) {
                clearGeneratorHandoff();
                [T_UID_KEY, DRAW_DONE_KEY, STARTED_KEY, PASS_OK_KEY, USERS_DONE_KEY, SCHEDULE_DONE_KEY].forEach((k) => localStorage.removeItem(k));
                sendQuizWelcomeEmail({tournament_uid: builtUid});
                queuePostCreateOffer(builtUid, err);
                ui?.done(data.eventKind === 'league'
                    ? `${areaBase}/leagues/${builtUid}/league?welcome=quiz`
                    : `${areaBase}/tournaments/${builtUid}/draws?welcome=quiz`);
                return;
            }
            const msg = String(err?.message || err?.error || '');
            if (/unauthenticated/i.test(msg)) {
                // Stale/expired session pretended to be logged in — clear it and ask for the
                // account instead of dead-ending on a raw auth error.
                try { await jwt.signOut(); } catch { /* stale token may 401 the logout too */ }
                ui?.step(7);
                return;
            }
            ui?.err(msg || 'Something went wrong — please try again.');
            ui?.step(6);
        }
    };

    // Event Pass checkout: Stripe returns to /quiz/ where the build (or the post-trim recovery)
    // resumes with the pass applied.
    const buyPass = async (fromRecovery = false) => {
        setPayBusy(true);
        recordResponse({plan: 'event_pass'}); // clicked to buy — logged before the Stripe redirect
        try {
            const {redirect_url} = await services.eventPass.checkout('/quiz/', 'quiz50'); // 50% first-pass launch offer
            window.location.href = redirect_url;
        } catch (e: any) {
            setPayBusy(false);
            if (fromRecovery) return; // stay on the done screen — the card's button just re-enables
            setBuilderError(e?.message || 'Could not start checkout.');
            setStep(6);
        }
    };

    // Continue a 9+ build that has just chosen a FREE route at the cap gate (trim to the cap, or
    // invite-to-earn). These skipped the wall on the way in, so they take it now: the Offerwall
    // only evaluates its URL rules on a page view, hence a real navigation — the resume effect
    // re-enters the build from the stash on the other side (signed in, STARTED_KEY still set).
    const continueFree = (h: GeneratorHandoff) => {
        if (h.eventKind !== 'americano' && !onActivatePath) {
            window.location.assign('/quiz/activate/');
            return;
        }
        runCreate(h);
    };

    // Cap gate: skip paying by pledging to get the players signed up. The pledge lifts the entry
    // cap (backend earn-start), so re-running the build adds the whole field; the draw is NOT
    // generated (it stays locked until 9+ entries sign up) and the build lands straight on the
    // event's Setup → Players tab, where EarnFreePassCard tracks progress and holds the unlock.
    const earnInstead = async () => {
        const stash = readGeneratorHandoff();
        const uid = localStorage.getItem(T_UID_KEY);
        if (!stash || !uid) return;
        setPayBusy(true);
        recordResponse({plan: 'earn'}); // chose invites over paying at the cap gate
        try {
            await services.eventPass.earnStart(uid.startsWith('el-') ? {league_uid: uid} : {tournament_uid: uid});
        } catch (e: any) {
            setPayBusy(false);
            setBuilderError(e?.message || 'Could not start the free unlock — please try again.');
            setStep(6);
            return;
        }
        sessionStorage.setItem('sp_quiz_plan', 'earn');
        setPayBusy(false);
        setCapInfo(null);
        continueFree(stash);
    };

    // Or trim to the free cap and finish without paying. This button promises the draw, so it
    // always takes the straight-to-draw path even if Advanced was selected earlier. The cut names
    // aren't discarded: they're kept (with the event uid) so the done screen can offer the pass to
    // restore them.
    const trimToCap = () => {
        const stash = readGeneratorHandoff();
        if (!stash || !capInfo) return;
        // League doubles ranks individuals, so each pair line is TWO entries against the cap.
        const perLine = stash.eventKind === 'league' && stash.leagueFormat === 'doubles_individual' ? 2 : 1;
        const kept = Math.max(1, Math.floor(capInfo.cap / perLine));
        const uid = localStorage.getItem(T_UID_KEY);
        if (uid && stash.teams.length > kept) {
            const rec: RecoveryStash = {uid, handoff: stash, kept, ts: Date.now()};
            try { localStorage.setItem(RECOVERY_KEY, JSON.stringify(rec)); } catch { /* storage off */ }
        }
        const trimmed: GeneratorHandoff = {...stash, teams: stash.teams.slice(0, kept), mode: 'basic'};
        stashGeneratorTournament(trimmed);
        restoreFromHandoff(trimmed); // the done screen counts what actually made it in
        sessionStorage.setItem('sp_quiz_plan', 'free');
        recordResponse({plan: 'free'}); // declined the pass at the cap gate
        setCapInfo(null);
        continueFree(trimmed);
    };

    // Pass was a plan choice (small field) — carry on free without paying.
    const skipPass = () => {
        const stash = readGeneratorHandoff();
        sessionStorage.setItem('sp_quiz_plan', 'free');
        recordResponse({plan: 'free'}); // declined the pass at the cap gate
        setCapInfo(null);
        if (stash) continueFree(stash);
    };

    // Six screens now: the audience question, then the five that build the event.
    const pct = Math.round(((Math.min(step, 4) + 2) / 6) * 100);
    const sport = (answers['2'] === 'other' && answers['2_other'])
        ? answers['2_other'].toLowerCase()
        : sportNames()[answers['2']] || 'your sport';
    const eventName = EVENT_NAMES[answers['1']] || 'event';

    // Social proof — shared by the done screen AND the pass screen: proof matters most at the
    // moment of payment, not after the decision is already made.
    const trustBlock = (
        <>
            <p className="trust-head">Trusted by real organisers, players and clubs</p>
            <div className="dupr-mark">
                <span>Official DUPR Partner</span>
                <i className="dupr-logo" role="img" aria-label="DUPR — Official Partner" />
            </div>
            {stats && (
                <div className="stat-row">
                    <div className="stat"><span className="n">{stats.matches.toLocaleString()}+</span><span className="l">Matches played</span></div>
                    <div className="stat"><span className="n">{stats.players.toLocaleString()}+</span><span className="l">Players</span></div>
                    <div className="stat"><span className="n">{(stats.clubs + stats.communities).toLocaleString()}+</span><span className="l">Clubs &amp; communities</span></div>
                </div>
            )}
            <div className="testi" key={tIdx}>
                <p className="tq">“{TESTIMONIALS[tIdx].quote}”</p>
                <p className="ta">{TESTIMONIALS[tIdx].name} <span>— {TESTIMONIALS[tIdx].role}</span></p>
                <div className="tdots">
                    {TESTIMONIALS.map((_, i) => (
                        <button key={i} className={`tdot${i === tIdx ? ' on' : ''}`} onClick={() => setTIdx(i)} aria-label={`Testimonial ${i + 1}`} />
                    ))}
                </div>
            </div>
        </>
    );

    const pain = answers['3'];
    const features = [
        PAIN_FEATURES[pain] || 'Live scoring, brackets and standings',
        // Registration / live URL / scheduling are always in the box — skip the ones the
        // pain-matched line above already covers so nothing reads twice.
        ...(pain !== 'signups' ? ['Player registration form — share one link, entries fill themselves in'] : []),
        ...(pain !== 'schedule' ? [`A live ${isLeague ? 'league' : 'tournament'} page with its own public URL`] : []),
        isLeague
            ? 'League standings ranked by win % or points — updated after every score'
            : 'Automatic match scheduling — rounds and courts sorted for you',
        `${sport.charAt(0).toUpperCase() + sport.slice(1)} formats and scoring, pre-configured`,
        'Free iOS & Android app for you and your players',
    ];

    return (
        <div className="sp-quiz">
            <title>Set up your event in 30 seconds — ScorePal</title>
            <meta name="robots" content="noindex" />
            {exitPrompt && step === 7 && (
                <div className="sp-exit" role="dialog" aria-modal="true" aria-labelledby="sp-exit-title"
                     onClick={(e) => { if (e.target === e.currentTarget) setExitPrompt(false); }}>
                    <div className="card">
                        {exitThanks ? (
                            <>
                                <h2 id="sp-exit-title">Thanks for telling us</h2>
                                <p>It helps us make ScorePal better for organisers like you.</p>
                            </>
                        ) : (
                            <>
                                <span className="kicker">One quick question</span>
                                <h2 id="sp-exit-title">Shame to see you leave</h2>
                                <p>You'll be surprised what ScorePal provides for <strong>FREE</strong>. Please help us understand why you're leaving?</p>
                                <div className="reasons">
                                    <button type="button" className="reason" onClick={() => answerExit('no_info')}>
                                        Don't want to provide information
                                    </button>
                                    <button type="button" className="reason" onClick={() => answerExit('unclear_value')}>
                                        Don't know what I'm getting
                                    </button>
                                    {exitOther ? (
                                        <div>
                                            <textarea autoFocus rows={3} maxLength={500} value={exitNote}
                                                      onChange={(e) => setExitNote(e.target.value)}
                                                      placeholder="Tell us in your own words…" aria-label="Other reason" />
                                            <button type="button" className="reason send" disabled={!exitNote.trim()}
                                                    onClick={() => answerExit('other')}>
                                                Send
                                            </button>
                                        </div>
                                    ) : (
                                        <button type="button" className="reason" onClick={() => setExitOther(true)}>
                                            Other
                                        </button>
                                    )}
                                </div>
                                <button type="button" className="stay" onClick={() => answerExit('continue')}>
                                    Continue to signup
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
            {exitPrompt && step !== 7 && (
                <div className="sp-exit" role="dialog" aria-modal="true" aria-labelledby="sp-exit-title"
                     onClick={(e) => { if (e.target === e.currentTarget) setExitPrompt(false); }}>
                    <div className="card">
                        <span className="kicker">{answeredCount > 0 ? `${answeredCount} of 5 answered` : 'Under a minute'}</span>
                        <h2 id="sp-exit-title">{step >= 5 ? 'Your event is almost ready' : 'Leave before your setup is done?'}</h2>
                        <p>
                            {step >= 5
                                ? 'The questions are done — one more step and your draw, standings and registration page are live.'
                                : `${5 - answeredCount} quick ${5 - answeredCount === 1 ? 'question' : 'questions'} left and ScorePal builds your event for you: draw, standings and a registration page.`}
                        </p>
                        <button type="button" className="stay" onClick={() => setExitPrompt(false)}>
                            {step >= 5 ? 'Finish my setup' : 'Keep going'}
                        </button>
                        <button type="button" className="leave" onClick={() => setExitPrompt(false)}>
                            No thanks
                        </button>
                    </div>
                </div>
            )}
            <style>{`
                .sp-quiz { min-height:100dvh; background:#fff;
                    font-family:Inter,sans-serif; color:#111; display:flex; flex-direction:column; align-items:center;
                    padding:20px 16px 48px; }
                .sp-quiz .wrap { width:100%; max-width:560px; }

                .sp-exit { position:fixed; inset:0; z-index:80; background:rgba(4,16,52,.72); backdrop-filter:blur(3px);
                    display:flex; align-items:center; justify-content:center; padding:20px; animation:spFadeUp .2s ease-out both; }
                .sp-exit .card { width:100%; max-width:420px; background:#fff; color:#0B1B3A; border-radius:18px; padding:26px 24px;
                    box-shadow:0 20px 60px rgba(0,0,0,.45); text-align:center; }
                .sp-exit .kicker { display:inline-block; background:#FFF6C7; color:#7A5A00; font-size:.75rem; font-weight:700;
                    letter-spacing:.08em; text-transform:uppercase; border-radius:99px; padding:4px 10px; margin-bottom:12px; }
                .sp-exit h2 { font-family:'Bebas Neue',sans-serif; font-size:2rem; line-height:1.05; letter-spacing:.02em; margin:0 0 8px; color:#0B1B3A; }
                .sp-exit p { color:#3b4a6b; font-size:.95rem; margin:0 0 18px; line-height:1.5; }
                .sp-exit .stay { display:block; width:100%; background:#F2C41E; color:#0B1B3A; border:0; border-radius:12px;
                    padding:14px 18px; font-size:1rem; font-weight:700; cursor:pointer; }
                .sp-exit .stay:hover { background:#ffd23f; }
                .sp-exit .leave { display:block; width:100%; background:transparent; border:0; color:#6b7a99; font-size:.85rem;
                    margin-top:10px; cursor:pointer; text-decoration:underline; }
                .sp-exit .reasons { display:flex; flex-direction:column; gap:8px; margin:0 0 14px; text-align:left; }
                .sp-exit .reason { display:block; width:100%; background:#F4F7FD; color:#0B1B3A; border:1.5px solid #DCE4F5;
                    border-radius:12px; padding:12px 14px; font-size:.95rem; font-weight:600; text-align:left; cursor:pointer; }
                .sp-exit .reason:hover { border-color:#F2C41E; background:#FFF9DF; }
                .sp-exit .reason.send { text-align:center; margin-top:8px; }
                .sp-exit .reason:disabled { opacity:.5; cursor:default; }
                .sp-exit textarea { width:100%; border:1.5px solid #DCE4F5; border-radius:12px; padding:10px 12px; font:inherit;
                    font-size:.95rem; color:#0B1B3A; resize:vertical; outline:none; }
                .sp-exit textarea:focus { border-color:#F2C41E; }

                @keyframes spLogoSpin { 0% {transform:rotateY(0) scale(.6); opacity:0} 60% {opacity:1}
                    100% {transform:rotateY(720deg) scale(1); opacity:1} }
                @keyframes spFadeUp { from {opacity:0; transform:translateY(12px)} to {opacity:1; transform:none} }
                .sp-splash { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:70dvh; text-align:center; }
                .sp-splash .pill { width:180px; height:180px; border-radius:50%; background:#ffffff; display:grid; place-items:center;
                    border:3px solid #0B3DB0; box-shadow:0 10px 30px rgba(11,61,176,.18), 0 0 0 8px rgba(242,196,30,.35);
                    animation:spLogoSpin 2.8s cubic-bezier(.2,.7,.3,1) both; }
                .sp-splash img { width:118px; height:118px; }
                .sp-splash .word { font-family:'Bebas Neue',sans-serif; color:#0B3DB0; font-size:3.2rem; letter-spacing:.06em; margin-top:18px;
                    animation:spFadeUp .6s ease-out 1.6s both; }
                .sp-splash .tag { color:#444; font-size:1.05rem; margin-top:4px; animation:spFadeUp .6s ease-out 2.2s both; }
                .sp-splash .ltrack { width:min(260px,70%); height:6px; margin-top:26px; border-radius:99px;
                    background:#e5e5e5; overflow:hidden; animation:spFadeUp .5s ease-out 2.6s both; }
                .sp-splash .lbar { height:100%; width:0; border-radius:99px; background:#F2C41E; animation:spLoad 4.6s ease-in-out 2.7s forwards; }
                @keyframes spLoad { to { width:100% } }
                .sp-splash .lmsg { color:#555; font-size:.85rem; margin-top:10px; animation:spFadeUp .5s ease-out 3s both; }

                .sp-quiz .qhead { margin:8px 0 18px; animation:spFadeUp .4s ease-out both; }
                .sp-quiz .pmeta { display:flex; justify-content:space-between; font-size:.8rem; color:#444; margin-bottom:6px; }
                .sp-quiz .ptrack { height:8px; border-radius:99px; background:#e8eefb; overflow:hidden; }
                .sp-quiz .pbar { height:100%; border-radius:99px; background:#F2C41E; transition:width .35s ease; }
                .sp-quiz .unlock { margin-top:10px; background:rgba(46,204,113,.16); border:1px solid rgba(46,204,113,.45);
                    color:#0f5132; font-size:.85rem; border-radius:10px; padding:8px 12px; animation:spFadeUp .35s ease-out both; }

                .sp-quiz h1 { font-family:'Bebas Neue',sans-serif; font-size:clamp(1.7rem,5.5vw,2.3rem); line-height:1.1;
                    letter-spacing:.02em; margin:6px 0 4px; color:#0B3DB0; }
                .sp-quiz .sub { color:#444; font-size:.95rem; margin:0 0 18px; }
                .sp-quiz .qstep { animation:spFadeUp .4s ease-out both; }
                .sp-quiz .opt { display:block; width:100%; text-align:left; background:#fff;
                    border:1.5px solid #111; color:#111; border-radius:14px; padding:15px 18px;
                    font-size:1rem; font-weight:600; margin-bottom:10px; cursor:pointer; transition:all .15s ease; }
                .sp-quiz .opt:hover, .sp-quiz .opt:active { background:#f3f6fd; border-color:#0B3DB0; transform:translateY(-1px); }
                .sp-quiz .opts2 { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-bottom:10px; }
                .sp-quiz .opts2 .opt { margin-bottom:0; padding:13px 14px; }
                @media (max-width:400px) { .sp-quiz .opts2 { grid-template-columns:1fr; } }
                .sp-quiz .opt .oemoji { display:inline-block; width:1.7em; font-size:1.1em; }
                .sp-quiz .qstep > .tin { margin-bottom:12px; }
                .sp-quiz .opt.osubmit:disabled { opacity:.45; cursor:default; transform:none; }
                .sp-quiz .qinter { animation:spFadeUp .35s ease-out both; cursor:pointer; padding-top:8px; }
                .sp-quiz .itrack { height:5px; border-radius:99px; background:#e8eefb; overflow:hidden; margin-bottom:14px; }
                .sp-quiz .ibar { height:100%; width:0; border-radius:99px; background:#F2C41E; animation:spInterBar 5s linear forwards; }
                @keyframes spInterBar { from { width:0 } to { width:100% } }
                .sp-quiz .iwrap { border-radius:14px; overflow:hidden; margin-top:12px;
                    border:1.5px solid #111; box-shadow:0 12px 30px rgba(0,0,0,.12); }
                .sp-quiz .icard { display:block; width:100%; height:auto;
                    animation:spKenBurns 5.2s linear forwards; transform-origin:center; }
                @keyframes spKenBurns { from { transform:scale(1) } to { transform:scale(1.12) } }
                .sp-quiz .imsg { margin-top:10px; text-align:center; font-size:.78rem; color:#555; }
                .sp-quiz .opt.sel { background:#FFF8DB; border-color:#0B3DB0; box-shadow:inset 0 0 0 1.5px #0B3DB0; }
                .sp-quiz .opt .odesc { display:block; font-weight:400; font-size:.82rem; color:#555; margin-top:2px; }

                .sp-quiz label { display:block; font-family:'Bebas Neue',sans-serif; font-size:1.15rem; letter-spacing:.05em;
                    color:#0B3DB0; margin:18px 0 7px; }
                .sp-quiz label:first-of-type { margin-top:4px; }
                .sp-quiz .warn { background:#fff8db; border:1px solid #111; color:#5c4400;
                    font-size:.85rem; border-radius:10px; padding:8px 12px; margin:-6px 0 12px; }
                .sp-quiz .pairs-preview { display:flex; flex-wrap:wrap; gap:6px; margin:-6px 0 12px; }
                .sp-quiz .pair-chip { background:#fff; border:1px solid #111; color:#111;
                    font-size:.8rem; font-weight:600; border-radius:99px; padding:4px 11px; display:inline-flex; align-items:center; gap:6px; }
                .sp-quiz .chip-x { background:none; border:none; color:#111; font-size:1rem; font-weight:800;
                    line-height:1; padding:0 2px; cursor:pointer; opacity:.75; }
                .sp-quiz .chip-x:hover { opacity:1; color:#c62835; }
                .sp-quiz .tin { width:100%; background:#fff; border:1.5px solid #111;
                    border-radius:12px; color:#111; font-size:1rem; padding:12px 14px; margin-bottom:14px; outline:none; }
                .sp-quiz .tin:focus { border-color:#0B3DB0; box-shadow:0 0 0 2px rgba(11,61,176,.25); }
                .sp-quiz textarea.tin { min-height:130px; resize:vertical; font-family:Inter,sans-serif; }
                /* Native select popups do not inherit the page's colours: pin the options to black on white. */
                .sp-quiz select.tin { appearance:auto; }
                .sp-quiz select.tin option { color:#0B1B4B; background:#fff; }
                .sp-quiz input[type=date].tin { color-scheme:light; }
                .sp-quiz .err { background:#fdecea; border:1px solid #c62835; color:#8a1c24;
                    font-size:.85rem; border-radius:10px; padding:8px 12px; margin-bottom:12px; }

                .sp-quiz .feat-card { background:#FFF8DB; border:2px solid #F2C41E; box-shadow:0 10px 28px rgba(242,196,30,.28);
                    border-radius:14px; padding:14px 18px; margin-bottom:18px; }
                .sp-quiz .feat-label { display:inline-block; background:#0B3DB0; color:#fff; font-weight:800; border-radius:99px; padding:5px 12px; font-size:.72rem; text-transform:uppercase; letter-spacing:.14em; margin:0 0 8px; }
                .sp-quiz .feat { display:flex; align-items:flex-start; gap:10px; font-size:.95rem; font-weight:600; margin-bottom:9px; color:#111; line-height:1.4; }
                .sp-quiz .feat > span:first-child { flex:0 0 22px; width:22px; height:22px; border-radius:50%; background:#0B3DB0;
                    color:#F2C41E; font-size:.8rem; font-weight:900; display:inline-flex; align-items:center; justify-content:center; margin-top:1px; }
                .sp-quiz .done-title span { background:linear-gradient(transparent 58%, #F2C41E 58%, #F2C41E 92%, transparent 92%);
                    -webkit-box-decoration-break:clone; box-decoration-break:clone; padding:0 4px; }
                .sp-quiz .offer-grid { display:grid; gap:12px; }
                @media (min-width:560px) { .sp-quiz .offer-grid { grid-template-columns:1fr 1fr; } }
                .sp-quiz .plan-card { position:relative; background:#fff; color:#111; border:1.5px solid #111; border-radius:16px; padding:20px 18px; }
                /* Done-screen roster: names the organizer typed, kept ones in, cut ones locked. */
                .sp-quiz .roster { display:flex; flex-direction:column; gap:4px; margin:10px 0 12px; text-align:left; }
                .sp-quiz .roster .r-item { display:flex; align-items:center; gap:8px; font-size:.9rem; padding:7px 12px;
                    border-radius:10px; background:#f2f5ff; color:#0B1B4B; }
                .sp-quiz .roster .r-item.r-locked { background:#eef0f4; color:#8b93a7; }
                .sp-quiz .roster .r-item.r-locked button { margin-left:auto; border:none; background:none; color:#0B3DB0;
                    font-weight:700; font-size:.8rem; cursor:pointer; padding:0; }
                .sp-quiz .free-reveal-link { display:block; margin:14px auto 0; border:none; background:none;
                    color:#444; font-size:.85rem; text-decoration:underline; cursor:pointer; }
                .sp-quiz .cg-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:8px; margin:8px 0 14px; }
                @media (max-width:640px) { .sp-quiz .cg-grid { grid-template-columns:repeat(2,1fr); } }
                .sp-quiz .cg-tile { display:flex; align-items:center; gap:7px; padding:10px 10px; border-radius:12px;
                    border:1.5px solid #111; background:#fff; color:#111;
                    font-size:.8rem; line-height:1.25; text-align:left; cursor:pointer; transition:all .15s; }
                .sp-quiz .cg-tile.on { border-color:#0B3DB0; background:#FFF8DB; box-shadow:inset 0 0 0 1.5px #0B3DB0; }
                .sp-quiz .cg-check { width:18px; height:18px; border-radius:6px; border:1.5px solid #111;
                    display:inline-flex; align-items:center; justify-content:center; flex:0 0 18px;
                    font-size:.75rem; font-weight:800; color:#fff; }
                .sp-quiz .cg-tile.on .cg-check { background:#0B3DB0; border-color:#0B3DB0; }
                .sp-quiz .cg-row { display:grid; grid-template-columns:repeat(2,1fr); gap:0 14px; }
                @media (max-width:520px) { .sp-quiz .cg-row { grid-template-columns:1fr; } }
                .sp-quiz .pass-feats { display:flex; flex-direction:column; gap:6px; margin:0 0 14px; text-align:left; }
                .sp-quiz .pass-feats > div { display:flex; gap:8px; font-size:.88rem; color:#22315e; line-height:1.4; }
                .sp-quiz .pass-feats > div > span:first-child { color:#16a34a; font-weight:800; }
                .sp-quiz .pass-valid { display:flex; align-items:center; gap:10px; margin:0 0 14px; padding:10px 12px;
                    background:#eaf1ff; border:1.5px solid #0B3DB0; border-radius:12px; font-size:.88rem;
                    color:#0B1B4B; line-height:1.45; text-align:left; }
                .sp-quiz .pass-valid .pv-icon { font-size:1.35rem; line-height:1; }
                .sp-quiz .pass-valid strong { color:#0B3DB0; }
                .sp-quiz .pass-ask { margin:10px 0 0; font-size:.78rem; color:#8b93a7; text-align:center; }
                .sp-quiz .pass-ask a { color:#0B3DB0; font-weight:600; text-decoration:underline; }
                .sp-quiz .plan-card.featured { border:2.5px solid #F2C41E; box-shadow:0 8px 24px rgba(11,61,176,.15); }
                .sp-quiz .badge { position:absolute; top:-11px; left:16px; background:#F2C41E; color:#06246E;
                    font-size:.68rem; font-weight:800; text-transform:uppercase; letter-spacing:.08em; border-radius:99px; padding:4px 10px; }
                .sp-quiz .discount { position:absolute; top:-13px; right:-6px; background:#E7333F; color:#fff;
                    font-size:.72rem; font-weight:900; letter-spacing:.04em; border-radius:99px; padding:5px 11px;
                    box-shadow:0 3px 10px rgba(231,51,63,.45); transform:rotate(3deg); }
                .sp-quiz .plan-price .was { font-size:1.3rem; color:#9aa7cc; margin-right:8px; }
                .sp-quiz .plan-offer { font-size:.78rem; font-weight:700; color:#C62835; margin:-2px 0 8px; }
                .sp-quiz .plan-name { font-weight:700; margin:0; }
                .sp-quiz .plan-price { font-family:'Bebas Neue',sans-serif; font-size:2.3rem; margin:2px 0 6px; color:#0B3DB0; }
                .sp-quiz .plan-desc { font-size:.88rem; color:#40507e; margin:0 0 14px; min-height:52px; }
                .sp-quiz .cta { width:100%; border-radius:12px; border:1.5px solid #0B3DB0; background:#fff; color:#0B3DB0;
                    font-weight:700; font-size:.98rem; padding:13px 0; cursor:pointer; }
                .sp-quiz .cta.primary { background:#F2C41E; color:#06246E; border-color:#F2C41E; font-weight:800; font-size:1.05rem;
                    padding:16px 0; box-shadow:0 5px 0 #c9a10f, 0 10px 24px rgba(242,196,30,.35); transition:transform .1s, box-shadow .1s; }
                .sp-quiz .cta.primary:hover { background:#ffd23f; }
                .sp-quiz .cta.primary:active { transform:translateY(3px); box-shadow:0 2px 0 #c9a10f; }
                .sp-quiz .cta.google { background:#fff; color:#111; border-color:#111; display:flex; align-items:center; justify-content:center; gap:10px; }
                .sp-quiz .or-div { text-align:center; margin:12px 0; color:#555; font-size:12px; text-transform:uppercase; letter-spacing:.14em; }
                .sp-quiz .plan-card .cta { border-color:#0B3DB0; color:#0B3DB0; background:#fff; }
                .sp-quiz .plan-card .cta.primary { background:#0B3DB0; color:#fff; border-color:#0B3DB0; box-shadow:none; font-size:.98rem; padding:13px 0; }
                .sp-quiz .cta:disabled { opacity:.6; }
                .sp-quiz .small-note { font-size:.78rem; color:#555; margin-top:10px; text-align:center; }

                /* "Your setup is ready" (sign-up, step 7, and done, step 10): the design from 2026-10-24 —
                   condensed headline with "is ready" highlighted, soft value card with yellow ticks, roomy
                   fields with icons, and the yellow press-down button. Scoped so other steps are unchanged. */
                .sp-quiz .signup h1.done-title { font-family:'Barlow Condensed','Bebas Neue',sans-serif; font-weight:800; text-transform:uppercase;
                    font-size:clamp(2rem,6vw,2.9rem); line-height:1.06; letter-spacing:-.01em; color:#103BB5; margin:4px 0 14px; }
                .sp-quiz .signup .done-title span { background:linear-gradient(transparent 19%, #FFCC1C 19%, #FFCC1C 94%, transparent 94%);
                    -webkit-box-decoration-break:clone; box-decoration-break:clone; padding:0 5px; white-space:nowrap; }
                .sp-quiz .signup .sub { color:#535b6b; font-size:1.05rem; line-height:1.6; margin:0 0 24px; }
                .sp-quiz .signup .sub strong { color:#081B47; }
                .sp-quiz .signup .feat-card { background:linear-gradient(135deg,#fff9db,#fffdf2); border:1.5px solid #f4c226; border-radius:20px;
                    padding:22px 24px; margin-bottom:24px; box-shadow:0 12px 30px rgba(217,167,0,.10); }
                .sp-quiz .signup .feat-label { display:inline-flex; background:#103BB5; padding:8px 16px; border-radius:30px; font-size:.72rem;
                    letter-spacing:.08em; margin:0 0 16px; }
                .sp-quiz .signup .feat { gap:14px; font-size:.9rem; font-weight:600; color:#081B47; line-height:1.5; margin-bottom:14px; }
                .sp-quiz .signup .feat:last-child { margin-bottom:0; }
                .sp-quiz .signup .feat > span:first-child { flex:0 0 28px; width:28px; height:28px; background:#FFCC1C; color:#103BB5;
                    font-size:1rem; font-weight:900; margin-top:-3px; }
                .sp-quiz .signup .cta.google { min-height:58px; border:1.5px solid #cdd4e0; border-radius:13px; color:#18202f; font-weight:700;
                    font-size:.95rem; gap:12px; transition:border-color .2s, box-shadow .2s; }
                .sp-quiz .signup .cta.google:hover { border-color:#103BB5; box-shadow:0 4px 15px rgba(16,59,181,.08); }
                .sp-quiz .signup .or-div { display:flex; align-items:center; gap:18px; margin:22px 0; color:#8b93a3; font-size:.8rem; letter-spacing:.1em; }
                .sp-quiz .signup .or-div::before, .sp-quiz .signup .or-div::after { content:''; flex:1; height:1px; background:#d9deea; }
                .sp-quiz .signup label { font-family:'Barlow Condensed','Bebas Neue',sans-serif; font-weight:700; font-size:1.06rem; letter-spacing:.01em;
                    text-transform:uppercase; color:#103BB5; margin:20px 0 8px; }
                .sp-quiz .signup label:first-of-type { margin-top:0; }
                .sp-quiz .signup .field-wrap { position:relative; }
                .sp-quiz .signup .tin { height:58px; margin:0; padding:0 48px 0 18px; border:1.5px solid #cdd4e0; border-radius:13px;
                    font-size:.95rem; color:#081B47; transition:border-color .2s, box-shadow .2s; }
                .sp-quiz .signup .tin::placeholder { color:#98a2b3; }
                .sp-quiz .signup .tin:focus { border-color:#103BB5; box-shadow:0 0 0 3px rgba(16,59,181,.09); }
                .sp-quiz .signup .field-icon { position:absolute; top:50%; right:18px; transform:translateY(-50%); color:#98a2b3; pointer-events:none; display:flex; }
                .sp-quiz .signup .field-icon svg { width:20px; height:20px; stroke:currentColor; fill:none; stroke-width:1.8; stroke-linecap:round; stroke-linejoin:round; }
                .sp-quiz .signup .cta.primary { margin-top:26px; min-height:62px; padding:17px 20px; background:#FFCC1C; border-color:#FFCC1C; border-radius:13px;
                    color:#081B47; font-weight:800; font-size:1.05rem; box-shadow:0 5px 0 #d9a900, 0 11px 22px rgba(217,169,0,.18); transition:transform .2s, box-shadow .2s, background .2s; }
                .sp-quiz .signup .cta.primary:hover { background:#ffd63c; transform:translateY(-2px); box-shadow:0 7px 0 #d9a900, 0 13px 25px rgba(217,169,0,.2); }
                .sp-quiz .signup .cta.primary:active { transform:translateY(3px); box-shadow:0 2px 0 #d9a900; }
                .sp-quiz .signup .cta.primary:disabled { transform:none; }
                .sp-quiz .signup .small-note { color:#777f8e; font-size:.75rem; line-height:1.7; margin-top:18px; }
                .sp-quiz .signup .small-note + .small-note { margin-top:4px; }
                .sp-quiz .signup .small-note a { color:#103BB5; text-decoration:underline; }
                @media (max-width:600px) {
                    .sp-quiz .signup .sub { font-size:.9rem; margin-bottom:20px; }
                    .sp-quiz .signup .feat-card { padding:18px 16px; border-radius:16px; }
                    .sp-quiz .signup .feat { font-size:.82rem; gap:12px; }
                    .sp-quiz .signup .feat > span:first-child { flex-basis:25px; width:25px; height:25px; font-size:.9rem; }
                    .sp-quiz .signup .cta.primary { font-size:.95rem; }
                }
                @media (prefers-reduced-motion: reduce) { .sp-quiz .signup * { transition:none !important; } }
                .sp-quiz .gc-row { display:flex; gap:8px; }
                .sp-quiz .opt.gc { flex:1; text-align:center; padding:11px 0; margin-bottom:14px; }
                .sp-quiz .trust-head { margin:26px 0 10px; text-align:center; font-size:.75rem; text-transform:uppercase;
                    letter-spacing:.16em; color:#555; }
                .sp-quiz .dupr-mark { display:flex; align-items:center; justify-content:center; gap:9px; margin:0 0 14px; }
                .sp-quiz .dupr-mark span { font-size:.68rem; font-weight:600; text-transform:uppercase;
                    letter-spacing:.14em; color:#555; }
                .sp-quiz .dupr-logo { display:block; width:74px; height:22px; background:#0B3DB0; -webkit-mask:url(/DUPR-White.png) no-repeat center / contain; mask:url(/DUPR-White.png) no-repeat center / contain; }
                .sp-quiz .stat-row { display:flex; gap:10px; justify-content:center; margin-bottom:14px; }
                .sp-quiz .stat { flex:1; max-width:160px; background:#fff; border:1.5px solid #111; border-top:5px solid #F2C41E;
                    border-radius:12px; padding:10px 6px; text-align:center; }
                .sp-quiz .stat .n { display:block; font-family:'Bebas Neue',sans-serif; font-size:1.6rem; line-height:1.1; color:#0B3DB0; }
                .sp-quiz .stat .l { display:block; font-size:.68rem; color:#555; margin-top:2px; }
                .sp-quiz .testi { background:#fff; border:1.5px solid #111; border-left:6px solid #F2C41E;
                    border-radius:14px; padding:16px 18px; animation:spFadeUp .45s ease-out both; }
                .sp-quiz .tdots { display:flex; gap:6px; justify-content:center; margin-top:12px; }
                .sp-quiz .tdot { width:8px; height:8px; border-radius:50%; border:none; padding:0; cursor:pointer;
                    background:#d4d4d4; }
                .sp-quiz .tdot.on { background:#F2C41E; }
                .sp-quiz .testi .tq { font-size:.9rem; font-style:italic; color:#222; line-height:1.55; margin:0 0 10px; }
                .sp-quiz .testi .ta { font-size:.85rem; font-weight:700; margin:0; }
                .sp-quiz .testi .ta span { font-weight:400; color:#555; }
                .sp-quiz .spin { display:inline-block; width:20px; height:20px; border-radius:50%; border:3px solid #d4d4d4;
                    border-top-color:#F2C41E; animation:spRotate .8s linear infinite; vertical-align:-4px; margin-right:10px; }
                @keyframes spRotate { to { transform:rotate(360deg) } }
                @media (prefers-reduced-motion: reduce) { .sp-quiz * { animation:none !important; } }
            `}</style>

            <div className="wrap">
                {phase === 'splash' && (
                    <div className="sp-splash">
                        <div className="pill"><img src="/favicon-180x180.png" alt="ScorePal" width={118} height={118} /></div>
                        <p className="word">Welcome to ScorePal</p>
                        <p className="tag">{entry ? PRICING_COPY.quizSplash(entrySport, entry.format) : 'The new way to manage your sporting events'}</p>
                        <div className="ltrack"><div className="lbar" /></div>
                        <p className="lmsg">Let's get started.</p>
                    </div>
                )}

                {/* Feature-card interstitial: 5 seconds of what their answers unlock, with its own
                    loading bar on top. Tap anywhere on it to skip straight to the next question. */}
                {phase === 'quiz' && step < 5 && interstitial != null && (
                    <div className="qinter" onClick={skipInterstitial} role="button" aria-label="Continue">
                        <div className="itrack"><div className="ibar" /></div>
                        {unlock && <div className="unlock">{unlock}</div>}
                        <div className="iwrap"><img className="icard" src={INTERSTITIALS[interstitial]} alt="" /></div>
                        <p className="imsg">Preparing your next question… tap to skip</p>
                    </div>
                )}

                {/* Which funnel is this? A club running events all season is better served by
                    /quiz-community, which sells the season rather than the single draw these
                    questions build. Asked once, before anything else. */}
                {phase === 'audience' && (
                    <>
                        <div className="qhead">
                            <div className="pmeta"><span>Step 1 of 6</span><span>17%</span></div>
                            <div className="ptrack"><div className="pbar" style={{width: '17%'}} /></div>
                        </div>
                        <div className="qstep">
                            <h1>Which one sounds like you?</h1>
                            <p className="sub">It decides what we show you next — the two are set up quite differently.</p>
                            <button
                                className="opt"
                                onClick={() => {
                                    recordResponse({answers: {...answers, audience: 'community'}});
                                    // Skip the community funnel's splash — they have just sat through
                                    // this one's, and a second logo animation reads as a failed click.
                                    // sessionStorage rather than a URL hash: AdSense's vignette
                                    // rewrites the hash to #google_vignette and silently killed it.
                                    sessionStorage.setItem('sp_cq_skip_splash', '1');
                                    window.location.assign('/quiz-community/');
                                }}
                            >
                                <span className="oemoji" aria-hidden="true">🏟️</span>I run regular sporting events for a club or community
                                <span className="odesc">Leagues, ladders and match nights, season after season.</span>
                            </button>
                            <button
                                className="opt"
                                onClick={() => {
                                    // Into the ANSWERS state, not just this one write: the
                                    // controller replaces the stored answers wholesale on every
                                    // upsert, so a key the later writes leave out is erased.
                                    keepAnswer('audience', 'one_off');
                                    setPhase('quiz');
                                }}
                            >
                                <span className="oemoji" aria-hidden="true">🏆</span>I'm organising a one-off event
                                <span className="odesc">A single tournament or social day — we'll build your draw in 30 seconds.</span>
                            </button>
                        </div>
                    </>
                )}

                {phase === 'quiz' && step < 5 && interstitial == null && (
                    <>
                        <div className="qhead">
                            {/* +2: the audience question ahead of these is step 1 of six. */}
                            <div className="pmeta"><span>Step {step + 2} of 6</span><span>{pct}%</span></div>
                            <div className="ptrack"><div className="pbar" style={{width: `${pct}%`}} /></div>
                            {unlock && <div className="unlock">{unlock}</div>}
                        </div>
                        <div className="qstep" key={step}>
                            <h1>{STEPS[step].q}</h1>
                            <p className="sub">{askShape
                                ? `Is ${otherSport.trim()} played by individual players, or by teams? We'll shape the setup around it.`
                                : askOtherSport ? "Type it in — we'll load the right formats and scoring." : STEPS[step].sub}</p>
                            {askShape ? (
                                <>
                                    <button className="opt" onClick={() => submitOtherShape('players')}>
                                        <span className="oemoji" aria-hidden="true">🙋</span>Players
                                        <span className="odesc">Singles or pairs — entrants are named people.</span>
                                    </button>
                                    <button className="opt" onClick={() => submitOtherShape('teams')}>
                                        <span className="oemoji" aria-hidden="true">👥</span>Teams
                                        <span className="odesc">Squads with a team name — fixtures and a points table.</span>
                                    </button>
                                    <button className="free-reveal-link" onClick={() => setAskShape(false)}>← Back</button>
                                </>
                            ) : askOtherSport ? (
                                <>
                                    <input className="tin" autoFocus maxLength={40} placeholder="e.g. Baseball, Hockey, Table tennis…"
                                        value={otherSport} onChange={(e) => setOtherSport(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') submitOtherSport(); }} />
                                    <button className="opt osubmit" disabled={!otherSport.trim()} onClick={submitOtherSport}>
                                        <span className="oemoji" aria-hidden="true">🏅</span>Continue
                                    </button>
                                    <button className="free-reveal-link" onClick={() => setAskOtherSport(false)}>← Back</button>
                                </>
                            ) : (
                                <>
                                    {/* Pre-answered from the ad's params: one tap carries it on; the options below still change it. */}
                                    {entry && step <= 1 && STEPS[step].options.some((o) => o.val === answers[String(step + 1)]) && (
                                        <button className="opt osubmit" onClick={() => answer(step + 1, answers[String(step + 1)])}>
                                            <span className="oemoji" aria-hidden="true">✅</span>Continue with {STEPS[step].options.find((o) => o.val === answers[String(step + 1)])!.label}
                                        </button>
                                    )}
                                    {/* The sport list is long enough to need two columns; the other steps stay stacked. */}
                                    <div className={step === 1 ? 'opts2' : undefined}>
                                        {STEPS[step].options.map((o) => (
                                            <button key={o.val} className="opt"
                                                onClick={() => step === 1 && o.val === 'other' ? setAskOtherSport(true) : answer(step + 1, o.val)}>
                                                <span className="oemoji" aria-hidden="true">{o.emoji}</span>{o.label}
                                            </button>
                                        ))}
                                    </div>
                                    {step > 0 && (
                                        <button className="free-reveal-link" onClick={() => { setUnlock(''); setStep(step - 1); }}>
                                            ← Back
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </>
                )}

                {/* 32+ teams: the choice BEFORE the concierge form — a hand from ScorePal, or carry
                    on self-serve. Big events used to be routed to the form with no way past it. */}
                {phase === 'quiz' && step === 12 && (
                    <div className="qstep">
                        <h1>More than 32 teams — that's a big one 🚀</h1>
                        <p className="sub">Events this size deserve a hand. How would you like to set it up?</p>
                        <button className="opt" onClick={() => setStep(11)}>
                            <span className="oemoji" aria-hidden="true">🤝</span>Ask ScorePal to assist
                            <span className="odesc">Tell us what you need — a real person sets it up with you, free.</span>
                        </button>
                        <button className="opt" onClick={() => startBuilder(answers)}>
                            <span className="oemoji" aria-hidden="true">🛠️</span>I'll set it up myself
                            <span className="odesc">Straight into the builder — you can still ask for help later.</span>
                        </button>
                        <button className="free-reveal-link" onClick={() => { setUnlock(''); setStep(4); }}>
                            ← Back
                        </button>
                    </div>
                )}

                {/* Concierge for 32+ events — a human-assisted setup request instead of self-serve. */}
                {phase === 'quiz' && step === 11 && (
                    <div className="qstep">
                        {cSent ? (
                            <div style={{textAlign: 'center', paddingTop: '14dvh'}}>
                                <h1>Request sent ✓</h1>
                                <p className="sub">One of our Event Organisers will be in touch shortly via email.</p>
                                <button className="cta primary" style={{marginTop: 18}} onClick={() => startBuilder(answers)}>
                                    Continue and set it up yourself →
                                </button>
                            </div>
                        ) : (<>
                            <h1>Let your personalised event organiser assist with your setup</h1>
                            <p className="sub">Big events deserve a hand — tell us what you need and we'll set it up with you.</p>

                            <label>Select what you need:</label>
                            <div className="cg-grid">
                                {['Scheduling', 'Registration Form', 'Collect Payments', 'Player Event Reminders',
                                  'Big Screen Live View', 'DUPR connected', 'Embed event on own website', 'Branding / Sponsorships'].map((n) => (
                                    <button key={n} type="button" className={`cg-tile${cNeeds.includes(n) ? ' on' : ''}`} onClick={() => toggleNeed(n)}>
                                        <span className="cg-check">{cNeeds.includes(n) ? '✓' : ''}</span>{n}
                                    </button>
                                ))}
                            </div>

                            <div className="cg-row">
                                <div>
                                    <label>When is your event?</label>
                                    <input className="tin" type="date" value={cDate} onChange={(e) => setCDate(e.target.value)} />
                                </div>
                                <div>
                                    <label>What sport?</label>
                                    <select className="tin" value={cSport} onChange={(e) => setCSport(e.target.value)}>
                                        <option value="">Choose…</option>
                                        {['Padel', 'Pickleball', 'Tennis', 'Football', 'Basketball', 'Badminton', 'Squash', 'Volleyball', 'Cricket', 'Other'].map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label>Type of tournament?</label>
                                    <select className="tin" value={cType} onChange={(e) => setCType(e.target.value)}>
                                        <option value="">Choose…</option>
                                        {['Knockout', 'Round robin', 'Groups + knockout', 'League / season', 'Americano / social', 'Not sure yet'].map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label>How many courts / pitches?</label>
                                    <input className="tin" type="number" min={1} value={cCourts} onChange={(e) => setCCourts(e.target.value)} />
                                </div>
                                <div>
                                    <label>How many players?</label>
                                    <input className="tin" type="number" min={1} value={cPlayers} onChange={(e) => setCPlayers(e.target.value)} />
                                </div>
                                <div>
                                    <label>How long will the event be?</label>
                                    <select className="tin" value={cDuration} onChange={(e) => setCDuration(e.target.value)}>
                                        <option value="">Choose…</option>
                                        {['One evening', 'Half a day', 'One day', 'A weekend', 'Over several weeks', 'Not sure yet'].map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                            </div>

                            <label>Player / team names <span style={{color: '#e5484d'}}>*</span></label>
                            <p className="sub" style={{margin: '0 0 8px', fontSize: 13}}>
                                One per line, or upload your list (CSV or text file). At least {C_MIN_ENTRANTS} needed.
                            </p>
                            <textarea className="tin" rows={7} value={cEntrantsText} onChange={(e) => setCEntrantsText(e.target.value)}
                                      placeholder={'Alex Smith\nJordan Lee\nSam Patel\n…'} aria-label="Player or team names" />
                            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, margin: '6px 0 14px'}}>
                                <label style={{margin: 0, cursor: 'pointer', color: '#1464f6', fontWeight: 700, fontSize: 14}}>
                                    ⬆ Upload list
                                    <input type="file" accept=".csv,.txt,text/csv,text/plain" style={{display: 'none'}}
                                           onChange={(e) => { uploadEntrants(e.target.files?.[0]); e.target.value = ''; }} />
                                </label>
                                <span style={{fontSize: 13, fontWeight: 700, color: cEntrants.length >= C_MIN_ENTRANTS ? '#12b76a' : '#667085'}}>
                                    {cEntrants.length} / {C_MIN_ENTRANTS}{cEntrants.length >= C_MIN_ENTRANTS ? ' ✓' : ' names'}
                                </span>
                            </div>

                            <label>Your name</label>
                            <input className="tin" value={cName} onChange={(e) => setCName(e.target.value)} autoComplete="name" />
                            <label>Email</label>
                            <input className="tin" type="email" value={cEmail} onChange={(e) => setCEmail(e.target.value)} autoComplete="email" />
                            <label>Anything else we should know?</label>
                            <textarea className="tin" value={cNotes} onChange={(e) => setCNotes(e.target.value)} placeholder="Format ideas, timings, sponsors, venue details…" />

                            {cErr && <div className="err">{cErr}</div>}
                            <button className="cta primary" onClick={sendConcierge} disabled={cBusy}>
                                {cBusy ? 'Sending…' : 'Send →'}
                            </button>
                            <button className="free-reveal-link" onClick={() => { setCErr(''); setStep(12); }}>
                                ← Back
                            </button>
                        </>)}
                    </div>
                )}

                {phase === 'quiz' && step === 10 && (
                    <div className="qstep signup">
                        <link rel="stylesheet" precedence="default" href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800&display=swap" />
                        <h1 className="done-title">Your {eventName} setup <span>is ready</span></h1>
                        <p className="sub">
                            {title || `Your ${sport} ${eventName}`} is live — {teamNames.length} {isTeamSport ? 'teams' : isLeague ? (leagueFormat === 'teams' ? 'teams' : leagueFormat === 'singles' ? 'players' : 'pairs') : (playType === 'doubles' ? 'pairs' : 'players')} in{mode === 'advanced' ? ', ready to configure' : isLeague ? (isTeamSport ? ' and the table is live' : ' and standings live') : ' and the draw is generated'}.
                        </p>
                        <div className="feat-card">
                            <p className="feat-label">Included in your setup</p>
                            {features.map((f) => <div key={f} className="feat"><span>✓</span><span>{f}</span></div>)}
                        </div>
                        <button className="cta primary" onClick={() => { if (doneDest) window.location.href = doneDest; }}>
                            {mode === 'advanced' ? 'Continue to full setup →' : isLeague ? 'Open my league →' : 'Show my draw →'}
                        </button>

                        {recovery && (() => {
                            const h = recovery.handoff;
                            const lines = h.teams.length - recovery.kept;
                            const isLg = h.eventKind === 'league';
                            const unit = isLg
                                ? (h.leagueFormat === 'teams' ? 'team' : h.leagueFormat === 'singles' ? 'player' : 'pair')
                                : (h.playType === 'doubles' ? 'pair' : h.playType === 'teams' ? 'team' : 'player');
                            const noun = `${lines} ${unit}${lines === 1 ? '' : 's'}`;
                            return (
                                <div className="plan-card featured" style={{marginTop: 20}}>
                                    <span className="badge">Saved from your list</span>
                                    <span className="discount">50% OFF</span>
                                    <p className="plan-name">{noun} didn't make the free cut</p>
                                    {/* Their own roster, with the cut names visibly locked — the loss is
                                        concrete names they typed, not an abstract count. */}
                                    <div className="roster">
                                        {h.teams.map((name, i) => i < recovery.kept ? (
                                            <div key={i} className="r-item"><span>✓</span><span>{name}</span></div>
                                        ) : (
                                            <div key={i} className="r-item r-locked">
                                                <span>🔒</span><span>{name}</span>
                                                <button type="button" onClick={() => buyPass(true)} disabled={payBusy}>Unlock</button>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="plan-price"><s className="was">$9.99</s> $4.99</p>
                                    <p className="plan-desc" style={{minHeight: 0}}>
                                        One Event Pass adds {lines === 1 ? 'it' : 'them all'} back
                                        {isLg ? ' into your league' : ' and rebuilds the draw'}, with unlimited entries for this event.
                                    </p>
                                    <button className="cta primary" onClick={() => buyPass(true)} disabled={payBusy}>
                                        {payBusy ? 'Opening checkout…' : `Add ${lines === 1 ? `the missing ${unit}` : `all ${noun}`} back — $4.99 →`}
                                    </button>
                                </div>
                            );
                        })()}

                        {trustBlock}
                    </div>
                )}

                {/* Pre-signup: the same build screens the post-signup flow shows (step 8's exact
                    look and phrases), staged here so the "ready" screen feels earned. */}
                {phase === 'quiz' && step === 6 && prepStage !== null && (() => {
                    const prepMsgs = [
                        `Creating your ${eventName}…`,
                        isAmericano ? 'Preparing your Americano…' : isTeamSport ? 'Adding your teams…' : pairEntry ? 'Adding your pairs…' : isLeague && leagueFormat === 'teams' ? 'Adding your teams…' : 'Adding your players…',
                        isLeague ? 'Getting your standings ready…' : 'Generating the draw…',
                    ];
                    return (
                        <div className="qstep" style={{textAlign: 'center', paddingTop: '18dvh'}}>
                            <h1><span className="spin" />{prepMsgs[Math.min(prepStage, prepMsgs.length - 1)]}</h1>
                            <p className="sub">This only takes a few seconds.</p>
                        </div>
                    );
                })()}

                {phase === 'quiz' && step === 6 && prepStage === null && (
                    <div className="qstep">
                        <h1>Set up your {isLeague ? 'league' : 'tournament'}</h1>
                        <p className="sub">{isTeamSport && isLeague
                            ? 'Name it, set your points and add your teams — the table updates itself as scores come in.'
                            : isLeague
                                ? 'Name it, pick a format and add your players — standings update themselves as scores come in.'
                                : 'Name it, pick a format and add your teams — the draw generates itself.'}</p>

                        {/* First, and on both routes: the name is required and must be their own. */}
                        <label>{isLeague ? 'League name' : isAmericano ? 'Americano name' : 'Tournament name'}</label>
                        <input ref={nameRef} className="tin" value={title} aria-invalid={!!nameError}
                               onChange={(e) => { setTitle(e.target.value); if (nameError) setNameError(''); }}
                               onFocus={(e) => { if (title === autoTitle.current) e.currentTarget.select(); }}
                               placeholder={isLeague ? 'e.g. Monday Night League' : isAmericano ? 'e.g. Friday Night Americano' : 'e.g. Friday Night Cup'} />
                        {nameError && <div className="err" data-quiz-name-error>{nameError}</div>}

                        {!isAmericano && <>
                            <label>How do you want to set it up?</label>
                            <button className={`opt${mode === 'basic' ? ' sel' : ''}`} onClick={() => setMode('basic')}>
                                Set up now<span className="odesc">{isLeague
                                    ? 'Quick — the fastest route to entry. Players in, standings live, record scores right away.'
                                    : 'Quick — the fastest route to entry. Your draw and fixtures are ready in seconds.'}</span>
                            </button>
                            <button className={`opt${mode === 'advanced' ? ' sel' : ''}`}
                                    onClick={() => { setMode('advanced'); generateDraw('advanced'); }}>
                                Go to platform<span className="odesc">Set up from there — registration, connecting payments, scheduling options and more.</span>
                            </button>
                        </>}

                        {/* The form appears only once "Set up now" is chosen — "Go to platform"
                            advances straight to the next step with defaults. */}
                        {(isAmericano || mode === 'basic') && <>
                        {isLeague ? (
                            <>
                                {/* Team sports are always named teams on a table, so the format is only asked
                                    of the players' sports. */}
                                {!isTeamSport && <>
                                <label>League format</label>
                                <button className={`opt${leagueFormat === 'singles' ? ' sel' : ''}`} onClick={() => setLeagueFormat('singles')}>
                                    Singles<span className="odesc">1v1 matches — players ranked by win % or points.</span>
                                </button>
                                <button className={`opt${leagueFormat === 'doubles_individual' ? ' sel' : ''}`} onClick={() => setLeagueFormat('doubles_individual')}>
                                    Doubles<span className="odesc">Doubles matches with individual rankings — everyone ranked on their own results.</span>
                                </button>
                                <button className={`opt${leagueFormat === 'teams' ? ' sel' : ''}`} onClick={() => setLeagueFormat('teams')}>
                                    Teams<span className="odesc">Fixed teams ranked on the league table.</span>
                                </button>
                                </>}

                                {/* The standings: win % or a points table, with the allocation under it. */}
                                <label>Ranked by</label>
                                <div className="gc-row">
                                    <button className={`opt gc${rankByChosen === 'win_percent' ? ' sel' : ''}`} onClick={() => setRankBy('win_percent')}>Win %</button>
                                    <button className={`opt gc${rankByChosen === 'points' ? ' sel' : ''}`} onClick={() => setRankBy('points')}>Points table</button>
                                </div>
                                {rankByChosen === 'points' && (
                                    <div className="gc-row">
                                        <div style={{flex: 1}}>
                                            <label>Points for a win</label>
                                            <input className="tin" type="number" min={0} max={10} value={pointsWin}
                                                   onChange={(e) => setPointsWin(Math.max(0, Math.min(10, Math.round(+e.target.value || 0))))} />
                                        </div>
                                        <div style={{flex: 1}}>
                                            <label>Points for a draw</label>
                                            <input className="tin" type="number" min={0} max={10} value={pointsDraw}
                                                   onChange={(e) => setPointsDraw(Math.max(0, Math.min(10, Math.round(+e.target.value || 0))))} />
                                        </div>
                                    </div>
                                )}

                                {/* The fixtures: built here as a round robin, or added by hand as games happen. */}
                                <label>Matches</label>
                                <button className={`opt${fixtures === 'auto' ? ' sel' : ''}`} onClick={() => setFixtures('auto')}>
                                    Create the fixtures for me<span className="odesc">A full round robin — everyone plays everyone once, dated from the next matchday. Add more rounds any time.</span>
                                </button>
                                <button className={`opt${fixtures === 'manual' ? ' sel' : ''}`} onClick={() => setFixtures('manual')}>
                                    I'll add matches myself<span className="odesc">No fixtures yet — record games as they are played and the standings follow.</span>
                                </button>
                            </>
                        ) : (
                            <>
                        {!isAmericano && !isTeamSport && <>
                        <label>Singles or doubles?</label>
                        <div className="gc-row">
                            <button className={`opt gc${playType === 'singles' ? ' sel' : ''}`} onClick={() => setPlayType('singles')}>Singles</button>
                            <button className={`opt gc${playType === 'doubles' ? ' sel' : ''}`} onClick={() => setPlayType('doubles')}>Doubles</button>
                        </div>
                        </>}

                        <label>Format</label>
                        {quizFormats(sportKey, isTeamSport).map((f) => (
                            <button key={f.val} className={`opt${format === f.val ? ' sel' : ''}`} onClick={() => setFormat(f.val)}>
                                {f.label}<span className="odesc">{f.desc}</span>
                            </button>
                        ))}

                        {format === 'groups_knockout' && (
                            <>
                                <label>How many groups?</label>
                                <div className="gc-row">
                                    {[2, 4, 8, 16].map((n) => (
                                        <button key={n} className={`opt gc${groupCount === n ? ' sel' : ''}`} onClick={() => setGroupCount(n)}>
                                            {n} groups
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                            </>
                        )}

                        {isAmericano ? (
                            <div className="warn" style={{marginTop: 8}}>
                                Next, on the Americano setup screen, you'll pick your players, courts and points per round —
                                and we'll generate the partner-rotation schedule automatically.
                            </div>
                        ) : (<>
                        <label style={{marginTop: 6}}>
                            {isTeamSport
                                ? <>Team names — one per line — {teamNames.length} teams</>
                                : <>Players — one per line{pairEntry ? ' (every two names make a pair)' : ''} — {teamNames.length} {pairEntry ? (isLeague && leagueFormat === 'teams' ? 'teams' : 'pairs') : 'players'}</>}
                            {mode === 'advanced' ? (isTeamSport ? ' — optional, teams can be added later' : ' — optional, players can register themselves') : ''}
                        </label>
                        <textarea ref={entrantsRef} className="tin" value={teamsText} onChange={(e) => setTeamsText(e.target.value)}
                                  placeholder={isTeamSport ? 'Red Lions\nBlue Rovers\nPark Athletic\nUnited Old Boys\n…' : 'Anna\nBen\nCarlos\nDana\n…'} />

                        {teamNames.length > 0 && (
                            <div className="pairs-preview">
                                {teamNames.map((p, i) => (
                                    <span key={i} className="pair-chip">
                                        {p}
                                        <button type="button" className="chip-x" aria-label={`Remove ${p}`}
                                                onClick={() => removeEntry(i)}>×</button>
                                    </span>
                                ))}
                            </div>
                        )}
                        {leftoverSingle && (
                            <div className="warn">
                                <strong>{leftoverSingle}</strong> is waiting for a partner — add one more name below them
                                (or write a pair on one line, e.g. <em>{leftoverSingle} &amp; Ben</em>).
                            </div>
                        )}
                        </>)}

                        {/* Beside the button, because that is where it is read: the builder is
                            longer than a phone screen, so an error at the top of the form left the
                            button looking dead — people tapped it again and again (Client Errors). */}
                        {builderError && <div className="err">{builderError}</div>}

                        <button className="cta primary" onClick={() => generateDraw()}>
                            {isAmericano ? 'Continue to Americano setup →' : isLeague ? 'Create my league →' : 'Generate draw →'}
                        </button>
                        {/* One-off visitors are told the size rule up front — tournament or league. */}
                        {!isAmericano && (
                            <p className="small-note">
                                {quizOrganizerPath(isLeague ? 'league' : 'tournament', answers.audience) === 'one_off'
                                    ? QUIZ_ENTRY_NOTE.oneOff()
                                    : QUIZ_ENTRY_NOTE.community}
                            </p>
                        )}
                        </>}
                        <button className="free-reveal-link" onClick={() => { setBuilderError(''); setStep(4); }}>
                            ← Back
                        </button>
                    </div>
                )}

                {phase === 'quiz' && step === 7 && (
                    <div className="qstep signup">
                        <link rel="stylesheet" precedence="default" href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800&display=swap" />
                        <h1 className="done-title">Your {eventName} setup <span>is ready</span></h1>
                        <p className="sub">{isAmericano
                            ? 'Create your free account, then pick your players, courts and points to build your Americano.'
                            : teamNames.length === 0
                                ? <><strong>{title.trim() || `Your ${sport} ${eventName}`}</strong> is ready — create your free account to set it up on the platform.</>
                                : <><strong>{title.trim() || `Your ${sport} ${eventName}`}</strong> is ready — {teamNames.length} {isTeamSport ? 'teams' : isLeague ? (leagueFormat === 'teams' ? 'teams' : leagueFormat === 'doubles_individual' ? 'pairs' : 'players') : (playType === 'doubles' ? 'pairs' : 'players')} in and your {isLeague ? (isTeamSport ? 'table' : 'standings') : 'draw'} prepared. Create your free account to open it.</>}</p>
                        {/* The value stack shown BEFORE the ask — the account form is underneath it. */}
                        <div className="feat-card">
                            <p className="feat-label">Included in your setup</p>
                            {features.map((f) => <div key={f} className="feat"><span>✓</span><span>{f}</span></div>)}
                        </div>
                        {suError && <div className="err">{suError}</div>}
                        <button className="cta google" type="button" disabled={suBusy}
                                onClick={() => startGoogleSignIn(isAmericano ? '/quiz/' : '/quiz/activate/')}>
                            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
                            Continue with Google
                        </button>
                        <div className="or-div">or</div>
                        <form onSubmit={submitSignup}>
                            <label htmlFor="su-name">Your name</label>
                            <div className="field-wrap">
                                <input id="su-name" className="tin" value={suName} onChange={(e) => setSuName(e.target.value)} autoComplete="name" placeholder="e.g. Alex Johnson" />
                                <span className="field-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><circle cx="12" cy="7.5" r="4"/><path d="M4 21v-2a8 8 0 0 1 16 0v2"/></svg></span>
                            </div>
                            <label htmlFor="su-email">Email</label>
                            <div className="field-wrap">
                                <input id="su-email" className="tin" type="email" value={suEmail} onChange={(e) => setSuEmail(e.target.value)} autoComplete="email" placeholder="e.g. alex@yourclub.com" />
                                <span className="field-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg></span>
                            </div>
                            <label htmlFor="su-password">Password</label>
                            <div className="field-wrap">
                                <input id="su-password" className="tin" type="password" value={suPassword} onChange={(e) => setSuPassword(e.target.value)} autoComplete="new-password" placeholder="Create a secure password" />
                                <span className="field-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><rect x="5" y="10" width="14" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg></span>
                            </div>
                            <button className="cta primary" type="submit" disabled={suBusy}>
                                {suBusy ? 'Creating your account…' : isAmericano ? 'Create account & set up Americano →' : isLeague ? 'Create & open my league →' : 'Create & show my draw →'}
                            </button>
                        </form>
                        <p className="small-note">By continuing you agree to ScorePal's <a href="/terms-conditions/" target="_blank" rel="noreferrer">Terms &amp; Conditions</a>.</p>
                        <p className="small-note">Already have an account? <a href="/login/">Log in</a> — your {isLeague ? 'league' : 'draw'} is saved and will finish when you return here.</p>
                    </div>
                )}

                {phase === 'quiz' && step === 8 && (
                    <div className="qstep" style={{textAlign: 'center', paddingTop: '18dvh'}}>
                        <h1><span className="spin" />{status || `Building your ${isLeague ? 'league' : 'tournament'}…`}</h1>
                        <p className="sub">This only takes a few seconds.</p>
                    </div>
                )}

                {phase === 'quiz' && step === 9 && capInfo && (
                    <div className="qstep">
                        {capInfo.reason === 'cap' ? (
                            <>
                                <h1>Unlock all {capInfo.count} {isLeague && leagueFormat !== 'teams' ? 'players' : 'teams'}</h1>
                                <p className="sub">The free plan covers {capInfo.cap} entries per event — your first {capInfo.cap} are already in.</p>
                            </>
                        ) : (
                            <>
                                <h1>Activate your event pass</h1>
                                <p className="sub">{isLeague
                                    ? 'One payment and your league opens instantly — unlimited players for this event.'
                                    : 'One payment and your draw generates instantly — unlimited teams for this event.'}</p>
                            </>
                        )}
                        {!showFreeOptions ? (
                            <>
                                <div className="plan-card featured" style={{marginBottom: 12}}>
                                    <span className="badge">One-off — no subscription</span>
                                    <span className="discount">50% OFF</span>
                                    <p className="plan-name">Unlimited event pass</p>
                                    <p className="plan-price"><s className="was">$9.99</s> $4.99</p>
                                    <p className="plan-offer">One time only — first pass half price!</p>
                                    {/* The full value stack, not just the cap lift — everything below is true of a
                                        pass-unlocked event today. */}
                                    <div className="pass-feats">
                                        <div><span>✓</span><span>Unlimited {isLeague && leagueFormat !== 'teams' ? 'players' : 'teams'} for this event</span></div>
                                        <div><span>✓</span><span>{isLeague ? 'League opens instantly' : 'Draw generated instantly'} — no waiting on signups</span></div>
                                        <div><span>✓</span><span>Live scoring, brackets &amp; standings on a shareable page</span></div>
                                        <div><span>✓</span><span>Big-screen live view for match day</span></div>
                                        <div><span>✓</span><span>Schedule games by time and venue — send to players with reminders</span></div>
                                        <div><span>✓</span><span>End-of-event organizer report</span></div>
                                        <div><span>✓</span><span>Collect entry fees by card — one signup covers the pass</span></div>
                                    </div>
                                    <div className="pass-valid">
                                        <span className="pv-icon">⭐</span>
                                        <span><strong>Don't worry — no need to use it right away.</strong> Your pass stays valid for a full year, ready whenever your event is.</span>
                                    </div>
                                    <button className="cta primary" onClick={() => buyPass()} disabled={payBusy}>
                                        {payBusy ? 'Opening checkout…' : `Pay $4.99 & show my ${isLeague ? 'league' : 'draw'} →`}
                                    </button>
                                    <p className="pass-ask">If we don't have what you need, <a href="mailto:support@scorepal.co?subject=ScorePal%20Event%20Pass%20question">let us know</a>.</p>
                                </div>
                                <button className="free-reveal-link" onClick={() => setShowFreeOptions(true)}>
                                    I'd rather not pay — show my free options
                                </button>
                            </>
                        ) : (
                            <>
                                {/* The earned pass is for racquet sports only (the server refuses the
                                    pledge for anything else), so other sports are never shown it. */}
                                {isRacquetSport(sportKey) && <div className="plan-card featured" style={{marginBottom: 12, borderColor: '#2ECC71'}}>
                                    <span className="badge" style={{background: '#2ECC71', color: '#fff'}}>No payment needed</span>
                                    <p className="plan-name">Earn it free — get your players signed up</p>
                                    <p className="plan-price" style={{color: '#16a34a'}}>FREE</p>
                                    <p className="plan-desc" style={{minHeight: 0}}>
                                        Add your full field and invite every player — by email or your event link.
                                        {isLeague ? ' Your league is created right away' : ' Your draw is generated right away'},
                                        but score recording stays locked until 9+ players have each signed themselves up.
                                    </p>
                                    <button className="cta primary" style={{background: '#2ECC71', borderColor: '#2ECC71', color: '#fff'}}
                                            onClick={earnInstead} disabled={payBusy}>
                                        Get players signed up →
                                    </button>
                                </div>}
                                {capInfo.reason === 'cap'
                                    ? <button className="cta" onClick={trimToCap}>Continue free with {capInfo.cap} {isLeague && leagueFormat !== 'teams' ? 'players' : 'teams'}</button>
                                    : <button className="cta" onClick={skipPass}>Continue on the free plan instead</button>}
                                <button className="free-reveal-link" onClick={() => setShowFreeOptions(false)}>
                                    ← Back to the $4.99 instant unlock
                                </button>
                            </>
                        )}
                        {trustBlock}
                    </div>
                )}
            </div>
        </div>
    );
}
